import React from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import UserSession from '@app/common/user-session';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import throughputFromLinks from './SiteHelper';
import { IconHeading } from '@app/components/IconHeading';
import { InfoSection } from '@app/components/InfoSection';
import { Spacer } from '@app/components/Spacer';
import { PatternflyTable } from '@app/components/PatternflyTable';
import {
  protocolOptions,
  directionOptions,
  getAddressTypeName,
  getLinkStatusName,
  getCarrierTypeName,
  shouldShowSatelliteControls,
  AddressType,
  LinkStatus,
  CarrierType,
} from '@app/common/constants';
import { capitalizeFirstLetter } from '@app/utils/utils';
import { sortable, cellWidth } from '@patternfly/react-table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionToggle,
  FormGroup,
  Spinner,
  Select,
  SelectOption,
  SelectVariant,
  Tabs,
  Tab,
  TabTitleIcon,
  TabTitleText,
  TextInput,
  FormSelect,
  FormSelectOption,
  Checkbox,
  Tooltip,
  Popover,
  Alert,
  AlertActionCloseButton,
  AlertVariant,
  Text,
  TextVariants,
  Flex,
  FlexItem,
  Modal,
  ModalVariant,
  ToggleGroup,
  ToggleGroupItem,
  Grid,
  GridItem,
  Button,
  Divider,
} from '@patternfly/react-core';
import { sortBy, reverseSortBy } from '@app/lib/functions';
import Site from '@app/api/site/site.actions';
import Tunnel from '@app/api/tunnel/tunnel.actions';
import SiteApi from '@app/api/site-api/site.actions';
import SpeedTest from '@app/api/speed-test/speed-test.actions';
import ControllerApi from '@app/api/controller-api/controller.actions';
import License from '@app/api/license/license.actions';
import SiteIcon from '@app/bgimages/sites-black.svg';
import WanIcon from '@app/bgimages/wans-black.svg';
import ACLProfileIcon from '@app/bgimages/service-catalog.svg';
import { getLbAlgoNameFromId } from './TunnelHelper';
import {
  AutomationIcon,
  BullhornIcon,
  ConnectedIcon,
  EnterpriseIcon,
  NetworkIcon,
  CatalogIcon,
  ClusterIcon,
  MapMarkerIcon,
  ExternalLinkAltIcon,
  ExclamationCircleIcon,
  SyncAltIcon,
  InfrastructureIcon,
  OutlinedClockIcon,
  TopologyIcon,
  DomainIcon,
  ServiceCatalogIcon,
  UserIcon,
  WrenchIcon,
  ArrowCircleDownIcon,
  ArrowCircleUpIcon,
  PencilAltIcon,
  PficonNetworkRangeIcon,
  CogIcon,
  TimesIcon,
} from '@patternfly/react-icons';
import { PageSection, Title } from '@patternfly/react-core';
import { formatDate, formatTimestampInLocalTime, networkCidrBlock } from '@app/lib/functions';
import { isValidIP, isValidNumber } from '@app/lib/validator';
import { SPEED_TEST_POLL_FREQ } from '@app/lib/constants';
import '../../components/InfoSection.css';
import './Site.css';
import { useSiteContext } from '@app/contexts/site.context';
import {
  selectClientCompanyById,
  selectControllerById,
  selectServerById,
  selectSiteById,
  selectWanById,
} from '@app/common/configuration/configuration.selectors';
import { push } from 'connected-react-router';
import { selectPartnerId, selectMetricsUrl } from '@app/common/session/session.selectors';
import UserSession from '@app/common/user-session';
import ACLProfile from '@app/api/aclprofile/aclprofile.actions';
import { fetchSiteViewACLData } from '@app/utils/aclUtils';
import { NotFound } from '../not-found/not-found';
import { ErrorView } from '../error/error';
import { actionCreators } from '@app/common/configuration/configuration.reducer';
import '../../app.css';
import './Site.css';
import {
  MdAccountTree,
  MdAltRoute,
  MdAppBlocking,
  MdForkLeft,
  MdInsertLink,
  MdLanguage,
  MdNewLabel,
  MdOutlineSecurity,
} from 'react-icons/md';
import Page from '@app/components/Page';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import { CardHeader, Col, Row, CardBody } from 'reactstrap';
import CustomCard from '@app/components/CustomCard';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Legend, ArcElement } from 'chart.js';
import loaderLogo from '@app/assets/images/logo/CloudAccess-Logo-BlkCloud.jpg';
import Loader from '@app/components/Loader';
import { LinkService, LinkCommandType } from '@app/api/link/link.service';
import axios from 'axios';
import MetricChart from '@app/components/MetricChart';
import DashboardApi from '@app/api/dashboard-api/dashboard.actions';
import { actionCreators as sessionActionCreators } from '@app/common/session/session.reducer';
import { useMapCache } from '@app/hooks/useMapCache';
import { useMapData } from '@app/contexts/mapData.context';
import { isUUID } from '@app/lib/validator';
import DownloadImage from '@app/components/DownloadImage';

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

enum TabIndex {
  // OSPF,
  BGP,
}

export const SiteView: React.FC = () => {
  //Get ID from URL params for instant access
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  
  // Log URL and params
  const allParams = useParams();
  React.useEffect(() => {
    // Extract ID manually from pathname as fallback
    const pathSegments = window.location.pathname.split('/');
    const manualId = pathSegments[pathSegments.length - 1];
    
  }, [id, allParams]);
  
  //Use context for full data, but don't block initial render  
  const { id: contextId, isLoading: contextLoading, isFailed, isSuccess } = useSiteContext();
  
  // Use context ID if available, fallback to URL params, then manual extraction
  const pathSegments = window.location.pathname.split('/');
  const manualId = pathSegments[pathSegments.length - 1];
  const fallbackId = (manualId && manualId.length > 30) ? manualId : null; // UUID-like length check
  
  const siteId = contextId || id || fallbackId;
  
  Chart.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Legend, ArcElement);
  const speedTestRef = React.useRef(null);
  const speedTestPollingRef = React.useRef<boolean>(false);
  const runningTestIdRef = React.useRef<string>('');

  const [confirmOpen, setConfirmOpen] = React.useState({ isOpen: false, rebootType: '' });
  const [expandedLinkId, setExpandedLinkId] = React.useState(null);
  const [rebootingLinks, setRebootingLinks] = React.useState<Record<string, boolean>>({});
  const [stowingLinks, setStowingLinks] = React.useState<Record<string, boolean>>({});
  const [confirmLinkAction, setConfirmLinkAction] = React.useState<{
    isOpen: boolean;
    linkId: string;
    linkName: string;
    provider: string;
    action: LinkCommandType | null;
  }>({ isOpen: false, linkId: '', linkName: '', provider: '', action: null });

  // Get data from both cache (instant) and Redux (full data)
  const partnerId = useSelector(selectPartnerId);
  const reduxSite = useSelector(selectSiteById(siteId)); // Full site data from Redux
  const { sites: cachedSites, controllers } = useMapCache();
  const cachedSite = cachedSites.find(s => s.id === siteId); // Basic data from cache
  
  // Use Redux data if available, fallback to cache for basic info
  const site = reduxSite || cachedSite; // Prefer full Redux data, fallback to cache
  
  // Ensure Redux site data loads for full nested relationships
  React.useEffect(() => {
    if (siteId && !reduxSite && !contextLoading) {
      dispatch(actionCreators.siteRequest(siteId));
    }
  }, [siteId, reduxSite, contextLoading, dispatch]);
  
  // Show basic info immediately, load nested data progressively
  const hasFullData = !!reduxSite; // Only Redux data has nested relationships
  const hasBasicData = !!(reduxSite || cachedSite);
  const wan = useSelector(selectWanById(site?.wanId));
  
  // SAFE NESTED ACCESS: Only access nested data if we have full Redux data
  const primaryTunnel = hasFullData ? site?.Tunnels?.find((tunnel) => tunnel.isPrimary === true) ?? site?.Tunnels?.[0] : null;
  const server = useSelector(selectServerById(primaryTunnel?.Server?.id));
  
  // SAFE NESTED ACCESS: Handle missing nested relationships gracefully
  const siteContact = hasFullData ? site?.SiteContacts?.[0] : null;
  const routedIpState = hasFullData ? ((site?.SiteRoutes?.length ?? 0) > 0 ? 'Enabled' : 'Disabled') : 'Loading...';
  const wanState = site?.wanId ? 'Enabled' : 'Disabled';

  const secondaryTunnel = hasFullData ? site?.Tunnels?.find((tunnel) => tunnel.isPrimary === false) : null;
  const standbyServer = useSelector(selectServerById(secondaryTunnel?.Server?.id));

  // SITE STATUS: Use centralized status from MapDataContext
  const centralizedSiteStatus = React.useMemo(() => {
    if (siteId && cachedSites.length > 0) {
      const cachedSiteForStatus = cachedSites.find(s => s.id === siteId);
      return cachedSiteForStatus?.status;
    }
    return undefined;
  }, [siteId, cachedSites]);

  React.useEffect(() => {
    if (centralizedSiteStatus !== undefined) {
      setSiteStatus(centralizedSiteStatus);
    } else if (hasFullData && site?.status !== undefined) {
      setSiteStatus(site.status);
    } else if (hasFullData && site) {
      // FALLBACK: Try to derive status from tunnel status or use default
      const tunnelStatus = site.Tunnels?.[0]?.status;
      const fallbackStatus = tunnelStatus !== undefined ? tunnelStatus : 3;
      setSiteStatus(fallbackStatus);
    } else if (hasFullData) {
    }
  }, [hasFullData, site?.status, centralizedSiteStatus, site]);


  const [infoTabIndex, setInfoTabIndex] = React.useState(0);
  const [downloadFooterStatus, setDownloadFooterStatus] = React.useState<{ title: string; status: string }>({ title: '', status: '' });
  const [activeDownloadTitle, setActiveDownloadTitle] = React.useState('');
  const [cancelDownloadRequestId, setCancelDownloadRequestId] = React.useState(0);
  const [aclProfiles, setAclProfiles] = React.useState([]);
  const [siteLans, setSiteLans] = React.useState([]);
  const [siteLansLoading, setSiteLansLoading] = React.useState(true); // LAN loading state

  const downloadItems = React.useMemo(() => ([
    {
      title: 'CloudAccess Image',
      links: [
        { label: 'amd64', type: 'CAImage', title: 'CloudAccess Image (amd64)' },
      ],
    },
    {
      title: 'CloudAccess Flex',
      links: [
        { label: 'amd64', type: 'CAFlex', title: 'CloudAccess Flex (amd64)' },
        { label: 'arm64', type: 'CAFlexARM64', title: 'CloudAccess Flex (arm64)' },
      ],
    },
  ]), []);

  const handleDownloadStatusChange = React.useCallback((title: string, update: { status: string; downloading: boolean }) => {
    if (update?.downloading && update?.status) {
      setActiveDownloadTitle(title);
      setDownloadFooterStatus({ title, status: update.status });
      return;
    }

    setActiveDownloadTitle((activeTitle) => activeTitle === title ? '' : activeTitle);
    setDownloadFooterStatus((current) => current.title === title ? { title: '', status: '' } : current);
  }, []);

  const handleDownloadStart = React.useCallback((title: string) => {
    setActiveDownloadTitle(title);
    setDownloadFooterStatus({ title, status: 'Starting...' });
  }, []);

  const handleCancelDownload = React.useCallback(() => {
    setCancelDownloadRequestId((current) => current + 1);
    setDownloadFooterStatus((current) => current.title ? { title: current.title, status: 'Cancelling...' } : current);
  }, []);

  const handleCancelComplete = React.useCallback(() => {
    setActiveDownloadTitle('');
    setDownloadFooterStatus({ title: '', status: '' });
  }, []);
  const [licenseId, setLicenseId] = React.useState('');
  const [licenseLoading, setLicenseLoading] = React.useState(true);
  
  // CRITICAL: Track if component is mounted to prevent state updates after unmount
  const isMounted = React.useRef(true);
  
  // CLEANUP: Set mounted to false when component unmounts and stop speed test polling
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
      speedTestPollingRef.current = false; // Stop any ongoing speed test polling
    };
  }, []);

  // DEBUG: Track key state changes
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
    }
  }, [aclProfiles?.length, siteLans?.length, siteLansLoading, licenseId, licenseLoading, speedTests?.length, isSpeedTestsLoading, siteStatus, siteApiData]);
  const [isSpeedTestModalOpen, setIsSpeedTestModalOpen] = React.useState(false);
  const [isSpeedTestResultsModalOpen, setIsSpeedTestResultsModalOpen] = React.useState(false);
  const [isSpeedTestDropdownOpen, setIsSpeedTestDropdownOpen] = React.useState(false);
  const [isCancelSpeedTestConfirmOpen, setIsCancelSpeedTestConfirmOpen] = React.useState(false);
  const [speedTestInterface, setSpeedTestInterface] = React.useState('Tunnel');
  const [completedSpeedTestInterface, setCompletedSpeedTestInterface] = React.useState('');
  const [speedTests, setSpeedTests] = React.useState([]);
  const [runningTestId, setRunningTestId] = React.useState('');
  const [speedTestIsRunning, setSpeedTestIsRunning] = React.useState(false);
  const [speedTestResultStatus, setSpeedTestResultStatus] = React.useState('');
  const [speedTestInputsDisabled, setSpeedTestInputsDisabled] = React.useState(false);
  const [speedTestResults, setSpeedTestResults] = React.useState({
    download: 0,
    upload: 0,
    jitter: 0,
    loss: 0,
    latency: 0,
  });
  const [samples, setSamples] = React.useState([]);
  const [download, setDownload] = React.useState(0);
  const [upload, setUpload] = React.useState(0);
  const [protocol, setProtocol] = React.useState('TCP');
  const [direction, setDirection] = React.useState('Bidirectional');
  const [duration, setDuration] = React.useState('10');
  const [occurences, setOccurences] = React.useState('1');
  const [cancelButtonText, setCancelButtonText] = React.useState('Cancel');
  // const [period, setPeriod] = React.useState('');
  const [isProtocolDropdownOpen, setIsProtocolDropdownOpen] = React.useState(false);
  const [isDirectionDropdownOpen, setIsDirectionDropdownOpen] = React.useState(false);
  const [advancedExpanded, setAdvancedExpanded] = React.useState('');
  const [routingConfig, setRoutingConfig] = React.useState('');
  const [activeTabIndex, setActiveTabIndex] = React.useState(TabIndex.BGP);
  const [isSpeedTestsLoading, setIsSpeedTestsLoading] = React.useState(true);
  const [siteStatus, setSiteStatus] = React.useState(0);
  const [siteMetrics, setSiteMetrics] = React.useState([]);
  const [siteApiData, setSiteApiData] = React.useState(null);
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;

  // Progressive loading states for better UX
  const [loadingStates, setLoadingStates] = React.useState({
    siteInfo: true,
    licenses: true,
    speedTests: true,
    siteLans: true,
    tunnels: true
  });

  // DEBUG: Log loading states changes
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
    }
  }, [loadingStates]);

  // INSTANT LOADING: Use cached data immediately 
  const { state: mapState } = useMapData();

  const partnerMetricsUrl = useSelector(selectMetricsUrl);
  const nocLink = React.useMemo(() => {
    const envLink = process.env.REACT_APP_NOC_LINK || '';
    if (partnerMetricsUrl) {
      try {
        const { pathname, search } = new URL(envLink);
        const base = partnerMetricsUrl.replace(/\/$/, '');
        return (base + pathname + search).replace('{{site_id}}', siteId ?? '');
      } catch {
        return envLink.replace('{{site_id}}', siteId ?? '');
      }
    }
    return envLink.replace('{{site_id}}', siteId ?? '');
  }, [partnerMetricsUrl, siteId]);

  const TitleMap = {
    [TabIndex.BGP]: 'BGP',
  };


  const { sites, controllers, refreshData, isStatusStale } = useMapCache(); 
  
  // Memoized data processing to prevent unnecessary re-renders
  const currentSite = React.useMemo(() => {
    return sites.find(site => site.id === siteId);
  }, [sites, siteId]);
  
  const centralizedSiteStatus = React.useMemo(() => {
    return currentSite?.status;
  }, [currentSite?.status]);

  // INSTANT LOADING: Reset loading states immediately when cache data is available
  React.useEffect(() => {
    if (mapState.cacheInitialized && currentSite && isMounted.current) {
      if (process.env.NODE_ENV === 'development') {
      }
      setLoadingStates(prev => ({
        ...prev,
        siteInfo: false,
        tunnels: false
      }));
    }
  }, [mapState.cacheInitialized, currentSite]);

  // CRITICAL: Initialize loading states based on cache availability
  React.useEffect(() => {
    if (mapState.cacheInitialized && !mapState.dashboardLoading && isMounted.current) {
      if (process.env.NODE_ENV === 'development') {
      }
      setLoadingStates(prev => ({
        ...prev,
        siteInfo: false,
        tunnels: false
      }));
    }
  }, [mapState.cacheInitialized, mapState.dashboardLoading]);

  // Cleanup: Set mounted flag to false on unmount
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // INSTANT LOADING: Only load data when cache is not available
  React.useEffect(() => {
    if (!siteId || !isUUID(siteId)) {
      return;
    }
    
    // INSTANT: Skip API calls if cache is ready, but still load when full data arrives
    if (mapState.cacheInitialized && currentSite && !hasFullData) {
      // Don't set loading to false yet - we need full data for API calls
      return;
    }
    
    // FULL DATA: Only make API calls when we have full site data
    if (!hasFullData) {
      return;
    }
    
    const loadSiteData = async () => {
      // Fetch license and ACL data independently so one failure doesn't block the other
      const licensePromise = License.getAll(partnerId).catch(error => {
        console.error('Error loading license data:', error);
        return [];
      });

      const aclPromise = fetchSiteViewACLData(siteId, site?.wanId || currentSite?.wanId).catch(error => {
        console.error('Error loading ACL data:', error);
        return { allProfiles: [], siteProfiles: [], wanProfiles: [], combinedProfiles: [] };
      });

      try {
        const [licenseList, aclData] = await Promise.all([licensePromise, aclPromise]);

        const siteLicenseId = (licenseList || []).filter((s) => s.assigned_to === siteId);
        const allAcls = aclData.combinedProfiles;

        if (isMounted.current) {
          setLicenseId(siteLicenseId.length ? siteLicenseId[0].id : 'Unlicensed');
          setAclProfiles(allAcls);
          setLicenseLoading(false);

          setLoadingStates(prev => ({ ...prev, licenses: false }));
        }
      } catch (error) {
        console.error('Error loading site data:', error);
        if (isMounted.current) {
          setLicenseLoading(false);
          setLoadingStates(prev => ({ ...prev, licenses: false }));
        }
      }
    };

    loadSiteData();
  }, [siteId, partnerId, currentSite?.wanId, mapState.cacheInitialized, currentSite, hasFullData]); 

  // INSTANT LOADING: Skip speed tests API call for instant load
  React.useEffect(() => {
    if (!siteId) {
      return;
    }
    
    // CRITICAL: Don't reload speed tests while one is actively running
    // This prevents the running test from appearing in the list during status updates
    if (speedTestPollingRef.current) {
      return;
    }
    
    // INSTANT: Skip speed tests loading if cache is ready, but load when full data arrives
    if (mapState.cacheInitialized && currentSite && !hasFullData) {
      setSpeedTests([]); // Set empty for now, will load when full data arrives
      return;
    }
    
    // FULL DATA: Only load speed tests when we have full site data
    if (!hasFullData) {
      return;
    }
    
    const loadSpeedTests = async () => {
      try {
        const allSpeedTests = await SpeedTest.getAll(siteId);
        
        const sortedSpeedTests = reverseSortBy(allSpeedTests.tests, 'test_date');
        
        if (isMounted.current) {
          setSpeedTests(sortedSpeedTests);
        }
      } catch (error) {
        console.error(' Error loading speed tests:', error);
        if (isMounted.current) {
          setSpeedTests([]);
        }
      } finally {
        if (isMounted.current) {
          setIsSpeedTestsLoading(false);
          // Update progressive loading state
          setLoadingStates(prev => ({ ...prev, speedTests: false }));
        }
      }
    };

    loadSpeedTests();
  }, [siteId, mapState.cacheInitialized, currentSite, hasFullData]); 


  // INSTANT LOADING: Skip site LANs API call for instant load
  React.useEffect(() => {
    
    if (!siteId) {
      return;
    }
    
    // INSTANT: Show cached data immediately, but still load full data in background
    if (mapState.cacheInitialized && site && !hasFullData) {
      // Don't set loading to false yet - we still need full data
      // Just show what we can from cache
      setSiteLans([]); // Cache doesn't have SiteLans
      return;
    }
    
    // FULL DATA: Process nested relationships when Redux data is available
    if (hasFullData) {
      
      setSiteLansLoading(false);
      setLoadingStates(prev => {
        return { ...prev, siteLans: false };
      });
      
      const tunnels = site?.Tunnels || [];
      const activeTunnel = tunnels?.filter((tunnel) => tunnel.isPrimary === true);
      
      
      const lansToSet = site?.SiteLans || [];
      setSiteLans(lansToSet);
      
      // Set all links as expanded by default
      if (activeTunnel.length && activeTunnel[0].Links && activeTunnel[0].Links.length > 0) {
        setExpandedLinkId(activeTunnel[0].Links[0].id);
      }
      return;
    }
    
    // FALLBACK: Only run if we don't have full data and need to fetch LANs separately
    (async function () {
      let lans = [];
      setSiteLansLoading(true); // Start loading
      
      try {
        lans = await Site.getSiteLansBySiteId(siteId);
      } catch (error) {
        console.warn('SiteView: Failed to load site LANs:', error);
        lans = [];
      }

      if (isMounted.current) {
        setSiteLans(lans);
        setSiteLansLoading(false); 
        setLoadingStates(prev => ({ ...prev, siteLans: false }));
      }
    })();
  }, [siteId, site, mapState.cacheInitialized, hasFullData]); 

  React.useEffect(() => {

    

  }, [currentSite]);

 
  React.useEffect(() => {
    if (centralizedSiteStatus !== undefined) {
      setSiteStatus(centralizedSiteStatus);
    }
  }, [centralizedSiteStatus]);

  // Refresh controller data if it's stale when viewing site details
  React.useEffect(() => {
    if (id && isStatusStale && controllers.length > 0) {
      refreshData();
    }
  }, [id, isStatusStale, refreshData, controllers.length]);

  React.useEffect(() => {
    // Only process SiteLans if we have full Redux data
    if (hasFullData && site?.SiteLans && site.SiteLans.length > 0 && siteLansLoading) {
      setSiteLans(site.SiteLans);
      setSiteLansLoading(false);
    } else if (hasFullData && site && (!site.SiteLans || site.SiteLans.length === 0) && siteLansLoading) {
      setSiteLans([]);
      setSiteLansLoading(false);
    }
  }, [site?.SiteLans, siteLansLoading, hasFullData]);

  React.useEffect(() => {
    if (!siteId) {
      return;
    }
    
    fetchSiteApiData();
  }, [siteId, site?.id, site?.updated_at]);

  const fetchSiteApiData = async () => {

    if (!siteId) {
      return;
    }
    
    try {
      const token = UserSession.getToken();
      if (!token) {
        return;
      }

      const apiUrl = `${process.env.REACT_APP_API_URL}/sites/${siteId}`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      
      // MEMORY LEAK FIX: Only update state if component is still mounted
      if (isMounted.current) {
        setSiteApiData(response.data);
      }
    } catch (error) {
      console.error(' SiteView: Failed to fetch site API data:', error);
    }
  };

  const getTunnelInfo = React.useCallback(() => {
    return [
      { label: 'Loss Threshold', value: primaryTunnel?.lossTolerance + '%' },
      { label: 'Encryption', value: primaryTunnel?.isClearTextData ? 'Disabled' : 'Enabled' },
      { label: 'MTU Size', value: primaryTunnel?.autoMTU ? 'Auto' : 'Custom ' + primaryTunnel?.mtu },
      { label: 'Timeout', value: (primaryTunnel?.timeout || '1') + 's' },
      { label: 'Reorder Buffer Time', value: primaryTunnel?.reorderBufferTime + ' ms' },
      { label: 'Link Port Range', value: primaryTunnel?.portRangeStart + '-' + primaryTunnel?.portRangeEnd },
      { label: 'Bonding Algorithm', value: getLbAlgoNameFromId(primaryTunnel?.lbAlgo) },
      { label: 'Flowlet', value: primaryTunnel?.flowlet ? 'Enabled' : 'Disabled' },
    ];
  }, [primaryTunnel]);

  const getBusinessContinuityInfo = React.useCallback(() => {
    // SAFE: Only access nested data if we have full Redux data
    const linkConfig = hasFullData ? site?.Tunnels?.[0]?.Links?.find((link) => link.isPassthrough) : null;
    const interfaceName = linkConfig?.interfaceName ? `eth${linkConfig.interfaceName}` : 'N/A';
    return [{ label: 'Interface Port', value: interfaceName }];
  }, [site, hasFullData]);

  const getLanInfo = (lan) => {
    const subnetAddress = `${networkCidrBlock(`${lan?.lanSegmentIp4}${lan?.lanCidrIp4}`)}${lan?.lanCidrIp4}`;

    return [
      { label: 'LAN Subnet', value: subnetAddress },
      { label: 'LAN Gateway IP', value: lan?.lanSegmentIp4 },
      { label: 'Primary DNS', value: lan?.dnsServer1 },
      { label: 'Secondary DNS', value: lan?.dnsServer2 },
      { label: 'DMZ Forwarding IP', value: lan?.isDMZ ? lan?.ip4DMZ : 'Disabled' },
      //{ label: 'VLAN', value: lan?.vlanId }
    ];
  };

  const getWifiInfo = (lan) => {
    const mode =
      lan?.apMode === 'mode_5'
        ? {
            speed: '5',
            channel: lan?.ap5Channel,
            ssid: lan?.ap5SSID,
            password: lan?.ap5Password,
          }
        : {
            speed: '2.4',
            channel: lan?.apChannel,
            ssid: lan?.apSSID,
            password: lan?.apPassword,
          };

    return [
      { label: mode.speed + ' GHz SSID', value: mode.ssid },
      { label: mode.speed + ' GHz Password', value: mode.password },
      { label: mode.speed + ' GHz Channel', value: mode.channel },
    ];
  };

  const getMiscInfo = (lan) => {
    // Handle VLAN ID display
    const vlanDisplay = (lan?.vlanId === null || lan?.vlanId === undefined || lan?.vlanId === 0) 
      ? '' 
      : lan.vlanId.toString();
    
    return [{ label: 'VLAN', value: vlanDisplay }];
  };

  const getDhcpInfo = (lan) => {
    return [
      { label: 'IP Range Start', value: lan?.dhcpRangeStart },
      { label: 'IP Range End', value: lan?.dhcpRangeEnd },
      { label: 'Default Lease Time (s)', value: lan?.dhcpDefaultLease },
      { label: 'Max Lease Time (s)', value: lan?.dhcpMaxLease },
    ];
  };

  const getRoutedIpInfo = React.useCallback(() => {
    // Only access nested data if we have full Redux data
    if (!hasFullData || !site?.SiteRoutes) {
      return [];
    }
    return site.SiteRoutes.map((route) => {
      return { label: route.subnet, value: route.routerIP };
    });
  }, [site, hasFullData]);

  const getCompanyInfo = React.useCallback(() => {
    return [
      { label: 'Name', value: hasFullData ? site?.ClientCompany?.name : 'Loading...' },
      { label: 'Industry', value: hasFullData ? site?.ClientCompany?.industry : 'Loading...' },
    ];
  }, [site, hasFullData]);

  const getWanInfo = React.useCallback(() => {
    const accessibleIps = site?.ipAccessLevel === 'All' ? 'All' : site?.accessibleIps;
    return [
      { label: 'pWAN Name', value: wan?.name, id: site?.Wan?.id },
      { label: 'Accessible IPs', value: accessibleIps || 'Not configured' },
    ];
  }, [site, wan]);

  const getQuickLinks = React.useCallback(() => {
    if (site?.ClientCompany?.Quicklinks === undefined) {
      return '';
    }
    return site?.ClientCompany?.Quicklinks.map((quickLink, index) => {
      return (
        <div className="quick-link" key={quickLink.id}>
          Quick Link:{' '}
          <a href={quickLink.link as string} target="_blank">
            {quickLink.link}
          </a>
          <br />
          Description: <strong>{quickLink.description}</strong>
        </div>
      );
    });
  }, [site]);

  const getContactInfo = React.useCallback(() => {
    return [
      { label: 'First Name', value: siteContact?.firstName },
      { label: 'Last Name', value: siteContact?.lastName },
      { label: 'Email', value: siteContact?.email },
      { label: 'Phone', value: siteContact?.phone },
      { label: 'Ext.', value: siteContact?.ext },
      { label: 'Receives Alerts', value: siteContact?.emailAlert ? 'Yes' : 'No' },
    ];
  }, [siteContact]);

  const getLocationInfo = React.useCallback(() => {
    return [
      { label: 'Name', value: (site as any)?.name || '' },
      { label: 'Site Address', value: site?.address1 },
      { label: 'Address line 2', value: site?.address2 },
      { label: 'City', value: site?.city },
      { label: 'Country', value: site?.country },
      { label: 'State/Province', value: site?.state },
      { label: 'Postal Code', value: site?.postalCode },
    ];
  }, [site]);

  const getAclProfileInfo = () => {
    if (aclProfiles?.length > 0) {
      return aclProfiles.map((acl) => {
        let aclProfile = `/aclprofile/${acl.id}`;
        return { label: <Link to={aclProfile}>{acl.name}</Link>, value: acl.description };
      });
    }
    return [];
  };

  const getControllerInfo = React.useCallback(() => {
    
    // Use siteApiData if available
    if (siteApiData?.tunnels) {
      // Use siteApiData
      const activeTunnel = siteApiData.tunnels.find(t => t.is_active === true);
      const standbyTunnel = siteApiData.tunnels.find(t => t.is_active === false);
      
      const activeGateway = controllers?.find(ctrl => ctrl.id === activeTunnel?.server?.gateway_id);
      const standbyGateway = controllers?.find(ctrl => ctrl.id === standbyTunnel?.server?.gateway_id);
      
      return [
        { label: 'Tunnel', value: site?.isServerless ? 'Disabled' : 'Enabled' },
        { 
          label: 'Active Controller', 
          value: site?.isServerless ? 'N/A' : (activeGateway?.name || activeTunnel?.server?.gateway_id || 'N/A')
        },
        {
          label: 'Active Gateway IP',
          value: site?.isServerless ? 'N/A' : (activeTunnel?.server?.ip_4_address || 'N/A')
        },
        { 
          label: 'Standby Controller', 
          value: site?.isServerless ? 'N/A' : (standbyGateway?.name || standbyTunnel?.server?.gateway_id || 'N/A')
        },
        {
          label: 'Standby Gateway IP',
          value: site?.isServerless ? 'N/A' : (standbyTunnel?.server?.ip_4_address || 'N/A')
        },
      ];
    } else {
      // Use Redux data when siteApiData is not available
      const activeController = controllers?.find(ctrl => ctrl.id === server?.gatewayId);
      const standbyController = controllers?.find(ctrl => ctrl.id === standbyServer?.gatewayId);
      
      return [
        { label: 'Tunnel', value: site?.isServerless ? 'Disabled' : 'Enabled' },
        { 
          label: 'Active Controller', 
          value: site?.isServerless ? 'N/A' : (activeController?.name || server?.gatewayId || 'N/A')
        },
        {
          label: 'Active Gateway IP',
          value: site?.isServerless ? 'N/A' : (server?.ip4Address || 'N/A')
        },
        { 
          label: 'Standby Controller', 
          value: site?.isServerless ? 'N/A' : (standbyController?.name || standbyServer?.gatewayId || 'N/A')
        },
        {
          label: 'Standby Gateway IP',
          value: site?.isServerless ? 'N/A' : (standbyServer?.ip4Address || 'N/A')
        },
      ];
    }
  }, [site, siteApiData, controllers, hasFullData, primaryTunnel, secondaryTunnel, server, standbyServer]);

  const handleTabClick = (e, index) => {
    setActiveTabIndex(index);
  };

  const editSite = React.useCallback(
    (event) => {
      dispatch(push(`/site/edit/${siteId}`));
    },
    [dispatch, siteId]
  );

  const scrollToSpeedTest = () => {
    if (speedTestRef.current) {
      speedTestRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openSpeedTestModal = async (e, testId) => {
    setIsSpeedTestModalOpen(true);

    if (testId) {
      const speedTest = await SpeedTest.getById(siteId, testId);
      const params = speedTest.test.params;

      setProtocol(params.protocol.toUpperCase());
      setDirection(capitalizeFirstLetter(params.direction));
      setDuration(params.duration);
      setOccurences(params.recurrence.repeat);
      // setPeriod(params.recurrence.period);
      setSpeedTestInterface(speedTest.test.device.name);
      setSpeedTestInputsDisabled(true);
      setRunningTestId(testId);
      runningTestIdRef.current = testId; // Store in ref
      speedTestPollingRef.current = true; // Mark polling as active
      setSpeedTestIsRunning(true);
      pollSpeedTest(testId);
    } else {
      initSpeedTest(true);
    }
  };

  const initSpeedTest = (setDefaults) => {
    setRunningTestId('');
    runningTestIdRef.current = ''; // Clear ref
    speedTestPollingRef.current = false; // Stop polling
    setSpeedTestInputsDisabled(false);
    setSpeedTestIsRunning(false);

    if (setDefaults) {
      setSpeedTestResultStatus('');
      setSpeedTestResults({ download: 0, upload: 0, jitter: 0, loss: 0, latency: 0 });
      setProtocol('TCP');
      setDirection('Bidirectional');
      setDuration('10');
      setOccurences('1');
      // setPeriod('');
      setSpeedTestInterface('Tunnel');
      setAdvancedExpanded('');
    }
  };

  const handleSpeedTestResultsToggle = () => {
    setIsSpeedTestResultsModalOpen(false);
  };

  const handleCancelSpeedTestConfirmToggle = () => {
    setIsCancelSpeedTestConfirmOpen(false);
  };

  const runSpeedTest = async () => {
    const payload = {
      device_name: speedTestInterface,
      protocol: protocol.toLowerCase(),
      direction: direction.toLowerCase(),
      duration: parseInt(duration),
      recurrence: {
        repeat: parseInt(occurences),
        // period: period
      },
    };

    try {
      const result = await SpeedTest.start(siteId, payload);
      const testId = result?.data.test_id;

      if (testId) {
        setRunningTestId(testId);
        runningTestIdRef.current = testId; // Store in ref for polling
        speedTestPollingRef.current = true; // Mark polling as active
        setSpeedTestIsRunning(true);
        pollSpeedTest(testId);
        setSpeedTestResultStatus('');
        setSpeedTestResults({ download: 0, upload: 0, jitter: 0, loss: 0, latency: 0 });
      } else {
        setSpeedTestResultStatus('StartFailed');
        setSpeedTestIsRunning(false);
        speedTestPollingRef.current = false;
      }
    } catch (error) {
      console.log(error);
      setSpeedTestResultStatus('StartFailed');
      setSpeedTestIsRunning(false);
      speedTestPollingRef.current = false;
    }
  };

  const pollSpeedTest = async (testId) => {
    // Protect against multiple concurrent polls
    if (!speedTestPollingRef.current) {
      return;
    }

    try {
      let poll = await SpeedTest.getById(siteId, testId);
      let testStatus = capitalizeFirstLetter(poll.test.status);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      while ((testStatus === 'Pending' || testStatus === 'Running') && speedTestPollingRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 5000)); // SPEED_TEST_POLL_FREQ
        
        // Check if polling is still active before making the API call
        if (!speedTestPollingRef.current) {
          break;
        }
        
        poll = await SpeedTest.getById(siteId, testId);
        testStatus = capitalizeFirstLetter(poll.test.status);

        if (poll.test.samples) {
          const peakDownload = getPeak(poll.test.samples, 'rx_rate');
          const peakUpload = getPeak(poll.test.samples, 'tx_rate');
          const peakJitter = getPeak(poll.test.samples, 'jitter');
          const peakLatency = getPeak(poll.test.samples, 'latency');
          const peakLoss = getPeak(poll.test.samples, 'loss');

          setSpeedTestResults({
            download: peakDownload.rx_rate,
            upload: peakUpload.tx_rate,
            latency: peakLatency.latency,
            jitter: peakJitter.jitter,
            loss: peakLoss.loss,
          });
        }
      }

      setSpeedTestResultStatus(testStatus);

      if (testStatus === 'Completed' || testStatus === 'Failed' || testStatus === 'Cancelled') {
        setCancelButtonText('Close');
        setCompletedSpeedTestInterface(speedTestInterface);
        speedTestPollingRef.current = false; // Mark polling as complete
        initSpeedTest(false);
        
        // Reload speed tests list to include the completed test
        try {
          const allSpeedTests = await SpeedTest.getAll(siteId);
          const sortedSpeedTests = reverseSortBy(allSpeedTests.tests, 'test_date');
          if (isMounted.current) {
            setSpeedTests(sortedSpeedTests);
          }
        } catch (error) {
          console.error('Failed to reload speed tests after completion:', error);
        }
      }
    } catch (error) {
      console.error('Speed test polling error:', error);
      speedTestPollingRef.current = false;
      setSpeedTestResultStatus('Failed');
      setSpeedTestIsRunning(false);
    }
  };

  const getPeak = (samples, prop) => {
    const last = samples.length - 1;
    return sortBy(samples, prop)[last];
  };

  const cancelSpeedTest = async () => {
    // Stop polling immediately
    speedTestPollingRef.current = false;
    
    const testIdToCancel = runningTestIdRef.current || runningTestId;
    if (testIdToCancel !== '') {
      await SpeedTest.cancel(siteId, testIdToCancel);
    }

    // Reset all speed test state 
    initSpeedTest(true);
    
    // Reset button text 
    setCancelButtonText('Cancel');
    
    setIsCancelSpeedTestConfirmOpen(false);
    setIsSpeedTestModalOpen(false);
    
    // update speed tests list to show the cancelled test
    try {
      const allSpeedTests = await SpeedTest.getAll(siteId);
      const sortedSpeedTests = reverseSortBy(allSpeedTests.tests, 'test_date');
      if (isMounted.current) {
        setSpeedTests(sortedSpeedTests);
      }
    } catch (error) {
      console.error('Failed to reload speed tests after cancellation:', error);
    }
  };

  const cancelSpeedTestConfirm = async (isOpen) => {
    if (speedTestIsRunning) {
      setIsCancelSpeedTestConfirmOpen(isOpen);
    } else {
      setIsSpeedTestModalOpen(false);
    }
  };

  const onSelectInterfaceToggle = (isOpen) => {
    setIsSpeedTestDropdownOpen(isOpen);
  };

  const onSelectProtocolToggle = (isOpen) => {
    setIsProtocolDropdownOpen(isOpen);
  };

  const onSelectDirectionToggle = (isOpen) => {
    setIsDirectionDropdownOpen(isOpen);
  };

  const onInterfaceSelect = (value, event) => {
    setSpeedTestInterface(value);
  };

  const onProtocolSelect = (value, event) => {
    setProtocol(value);
  };

  const onDirectionSelect = (value, event) => {
    setDirection(value);
  };

  const handleDurationChange = (value) => {
    setDuration(value);
  };

  const handleOccurencesChange = (value) => {
    setOccurences(value);
  };

  // const handlePeriodChange = (value) => {
  //   setPeriod(value);
  // };

  const getDirectionOptions = () => {
    const dirOptions = directionOptions();
    return dirOptions.map((dir) => {
      return <FormSelectOption key={dir.value} value={dir.value} label={dir.value} />;
    });
  };

  const getProtocolOptions = () => {
    const protoOptions = protocolOptions();
    return protoOptions.map((proto) => {
      return <FormSelectOption key={proto.value} value={proto.value} label={proto.value} />;
    });
  };

  const getLinkInterfaceOptions = () => {
    const interfaces = [];
    
    // Add tunnel option first
    interfaces.push(<FormSelectOption key="tunnel" value="Tunnel" label="Tunnel" />);
    
    // Add link interfaces if available
    if (site?.Tunnels?.[0]?.Links && Array.isArray(site.Tunnels[0].Links)) {
      site.Tunnels[0].Links.forEach((link) => {
        if (link?.id && link?.interfaceName) {
          const interfaceName = 'eth' + link.interfaceName;
          interfaces.push(
            <FormSelectOption 
              key={link.id} 
              value={interfaceName}
              label={interfaceName}
            />
          );
        }
      });
    }
    
    return interfaces;
  };

  const getTestColumns = () => {
    return [
      { title: 'Run Date', transforms: [sortable] },
      { title: 'Download', props: { className: 'text-right' } },
      { title: 'Upload', props: { className: 'text-right' } },
      { title: 'Device', transforms: [sortable] },
      { title: 'Status', transforms: [sortable] },
      //{ title: 'LB Algorithm' },
      { title: 'Direction' },
      { title: 'Details' },
      { title: 'ID', props: { className: 'pf-m-hidden pf-m-hidden-on-md' } },
    ];
  };

  const showResults = async (e, testId) => {
    const results = await SpeedTest.getById(siteId, testId);
    const params = results.test.params;

    if (results.test.samples) {
      setDownload(getPeak(results.test.samples, 'rx_rate').rx_rate);
      setUpload(getPeak(results.test.samples, 'tx_rate').tx_rate);
    }

    setProtocol(params.protocol.toUpperCase());
    setDirection(capitalizeFirstLetter(params.direction));
    setDuration(params.duration);
    setOccurences(params.recurrence.repeat);
    // setPeriod(params.recurrence.period);
    setSpeedTestInterface(results.test.device.name);
    setSamples(results.test.samples);
    setIsSpeedTestResultsModalOpen(true);
  };

  const getSampleColumns = (isResults) => {
    return [
      { title: isResults ? 'Timestamp' : 'Run Date' },
      { title: 'Latency', props: { className: 'text-right' } },
      { title: 'Jitter', props: { className: 'text-right' } },
      { title: 'Loss', props: { className: 'text-right' } },
      { title: 'Download Rate (Mbps)', transforms: [cellWidth(15)], props: { className: 'text-right' } },
      { title: 'Bytes Downloaded (MB)', transforms: [cellWidth(15)], props: { className: 'text-right' } },
      { title: 'Upload Rate (Mbps)', transforms: [cellWidth(15)], props: { className: 'text-right' } },
      { title: 'Bytes Uploaded (MB)', transforms: [cellWidth(15)], props: { className: 'text-right' } },
      { title: 'ID', props: { className: 'pf-m-hidden pf-m-hidden-on-md' } },
    ];
  };

  const sampleFromItem = (item, isResults) => {
    return {
      cells: [
        { title: isResults ? Date.parse(item.test_time) : formatDate(item.test_time, false) },
        { title: item.latency },
        { title: item.jitter },
        { title: item.loss },
        { title: getMbps(item.rx_rate) },
        { title: getMB(item.rx_bytes) },
        { title: getMbps(item.tx_rate) },
        { title: getMB(item.tx_bytes) },
        { title: item.id, props: { className: 'pf-m-hidden pf-m-hidden-on-md' } },
      ],
    };
  };

  const getMbps = (bitsPerSecond) => {
    if (bitsPerSecond === 0) {
      return bitsPerSecond;
    }
    const mbps = bitsPerSecond / 1000000;
    return mbps.toFixed(1);
  };

  const getMB = (bytesPerSecond) => {
    if (bytesPerSecond === 0) {
      return bytesPerSecond;
    }
    const mb = bytesPerSecond / 1000000;
    return mb.toFixed(1);
  };

  const speedTestFromItem = (item) => {
    const device = item.device?.name?.toLowerCase();
    const isTunnelTest = device?.startsWith('ubond') || device === 'tunnel';
    const testStatus = capitalizeFirstLetter(item.status);

    if (!item.samples) {
      return {
        cells: [
          { title: sortableDate(item.test_date) },
          { title: '' },
          { title: '' },
          { title: isTunnelTest ? 'Tunnel' : device },
          { title: testStatus },
          // { title: 'N/A' },
          { title: capitalizeFirstLetter(item.params.direction) },
          {
            title: (
              <div
                key={`results-${item.test_id}`}
                className="pointer link"
                onClick={(e) => showResults(e, item.test_id)}
              >
                Show
              </div>
            ),
          },
          { title: item.test_id, props: { className: 'pf-m-hidden pf-m-hidden-on-md' } },
        ],
      };
    }

    const sample = item.samples[0];
    const last = item.samples.length - 1;
    const peakDownload = sortBy(item.samples, 'rx_rate')[last];
    const peakUpload = sortBy(item.samples, 'tx_rate')[last];

    return {
      cells: [
        { title: sortableDate(item.test_date) },
        { title: getMbps(peakDownload.rx_rate) },
        { title: getMbps(peakUpload.tx_rate) },
        { title: isTunnelTest ? 'Tunnel' : device },
        {
          title:
            testStatus === 'Running' ? (
              <div
                key={`speed-test-${item.test_id}`}
                className="pointer link"
                onClick={(e) => openSpeedTestModal(e, item.test_id)}
              >
                Running
              </div>
            ) : (
              testStatus
            ),
        },
        // { title: isTunnelTest ? sample?.algo : 'N/A' },
        { title: capitalizeFirstLetter(item.params.direction) },
        {
          title: (
            <div key={`results-${item.test_id}`} className="pointer link" onClick={(e) => showResults(e, item.test_id)}>
              Show
            </div>
          ),
        },
        { title: item.test_id, props: { className: 'pf-m-hidden pf-m-hidden-on-md' } },
      ],
    };
  };

  const sortableDate = (date) => {
    if (!date) {
      return '';
    }
    return date.replaceAll('T', ' ').split('.')[0];
  };

  const onAccordionToggle = (id) => {
    if (id === advancedExpanded) {
      setAdvancedExpanded('');
    } else {
      setAdvancedExpanded(id);
    }
  };

  const launchGrafanaDashboard = () => {
    let dynamicNocLink = nocLink;
    

    
    window.open(dynamicNocLink, '_blank');
  };

  const rebootGateway = React.useCallback(() => {
    dispatch(actionCreators.serverRebootRequest(server?.id));
    setConfirmOpen({ isOpen: false, rebootType: '' });
  }, [dispatch, server]);

  const rebootSite = React.useCallback(() => {
    dispatch(actionCreators.siteRebootRequest(siteId));
    setConfirmOpen({ isOpen: false, rebootType: '' });
  }, [dispatch, site, siteId]);

  const getLans = () => {
    
    return siteLans.length === 0
      ? []
      : siteLans?.map((lan, index) => {
          const dhcpState = lan?.isDhcpRequired ? 'Enabled' : 'Disabled';
          const wifiState = lan?.isAP ? 'Enabled' : 'Disabled';
          const dmzState = lan?.isDMZ ? 'Enabled' : 'Disabled';

          return (
            <Row className="font-12" key={lan.id || `lan-${index}`}>
              <Col>
                <CustomCard className="h-100">
                  <CardHeader>
                    <IconHeading icon={<NetworkIcon className="icon-medium" />} heading={lan.interfaceName} />
                  </CardHeader>
                  <CardBody>
                    <InfoSection keyValuePairs={getLanInfo(lan)} showHeading={false} />
                  </CardBody>
                </CustomCard>
              </Col>

              <Col>
                <CustomCard className="h-100">
                  <CardHeader>
                    <IconHeading icon={<ClusterIcon className="icon-medium" />} heading="DHCP" subHeading={dhcpState} />
                  </CardHeader>
                  <CardBody>
                    <InfoSection keyValuePairs={getDhcpInfo(lan)} showHeading={false} />
                  </CardBody>
                </CustomCard>
              </Col>

              <Col>
                <CustomCard className="h-100">
                  <CardHeader>
                    <IconHeading
                      icon={<ConnectedIcon className="icon-medium" />}
                      heading="WiFi Settings"
                      subHeading={wifiState}
                    />
                  </CardHeader>
                  <CardBody>
                    <InfoSection keyValuePairs={getWifiInfo(lan)} showHeading={false} />
                  </CardBody>
                </CustomCard>
              </Col>

              <Col>
                <CustomCard className="h-100">
                  <CardHeader>
                    <IconHeading icon={<CogIcon className="icon-medium" />} heading="Misc" subHeading={dmzState} />
                  </CardHeader>
                  <CardBody>
                    <InfoSection keyValuePairs={getMiscInfo(lan)} showHeading={false} />
                  </CardBody>
                </CustomCard>
              </Col>
            </Row>
          );
        });
  };

  const getLinks = React.useCallback(
    (isStandby) => {
      const links = site?.Tunnels?.[0]?.Links.filter((link) => link.isStandby === isStandby);
      const linkClass = isStandby ? 'standby-link' : '';

      return links?.map((link, index) => {
        // Initialize dataResetDate for each link
        let dataResetDate = '';

        if (link.dataLimitReset && link.dataLimit > 0) {
          dataResetDate = formatTimestampInLocalTime(link.dataLimitReset);
        }

        const dnsAddresses = link.staticDnsIp?.split(',') || [];
        const dataLimitGb = (link.dataLimit / 1000 / 1000).toFixed(2);

        const linkData = [
          { label: 'Link State', value: link.isEnabled ? 'Enabled' : 'Disabled' },
          { label: 'Interface', value: 'eth' + link.interfaceName },
          { label: 'Address Type', value: link.isDHCP ? 'Dynamic' : 'Static' },
          // @ts-ignore
          ...(!link.isDHCP && [{ label: 'Address', value: link.staticIp }]),
          // @ts-ignore
          ...(!link.isDHCP && link?.staticGatewayIp?.length > 0 && [{ label: 'Gateway', value: link.staticGatewayIp }]),
          // @ts-ignore
          ...(!link.isDHCP && isValidIP(dnsAddresses[0]) && [{ label: 'DNS - Primary', value: dnsAddresses[0] }]),
          // @ts-ignore
          ...(!link.isDHCP && isValidIP(dnsAddresses[1]) && [{ label: 'DNS - Secondary', value: dnsAddresses[1] }]),
          { label: 'Link Type', value: link?.type },
          // Only show VLAN ID if it's actually configured
          ...(link?.vlanId && parseInt(link.vlanId) > 0 ? [{ label: 'VLAN ID', value: link.vlanId }] : []),
          { label: 'Carrier', value: link.carrierName },
          {
            label: 'Data Limit Exceeded',
            value: link.dataLimit > 0 ? (link.dataLimitUsed >= link.dataLimit ? 'Yes' : 'No') : 'N/A',
          },
          // Only include Data Limit Reset Date if data limit is enabled
          ...(link.dataLimit > 0 ? [{ label: 'Data Limit Reset Date', value: dataResetDate }] : []),
          {
            label: 'Data Usage',
            value:
              link.dataLimit > 0 ? link.dataLimitUsed + ' KB of ' + dataLimitGb + ' GB' : link.dataLimitUsed + ' KB',
          },
        ];

        const isExpanded = expandedLinkId === link.id;

        // Format date for last updated
        const formatDate = (dateString) => {
          if (!dateString) return new Date().toLocaleString();
          try {
            return new Date(dateString).toLocaleString();
          } catch (e) {
            return dateString;
          }
        };

        // Find the matching link in the API response
        const apiLink = siteApiData?.tunnels?.[0]?.links?.find(
          (apiLink) => apiLink.id === link.id || apiLink.interface_name === link.interfaceName
        );

        // Function to get metadata fields from the link
        const getMetadataFields = (link) => {
          if (!link?.metadata || typeof link.metadata !== 'object') {
            return [];
          }
          
          // Convert the metadata object to an array of key-value pairs for display
          return Object.entries(link.metadata).map(([key, value]) => ({
            label: key,
            value: value
          }));
        };

        // Detailed information for expanded view
        const detailedLinkData = [
          {
            label: 'Provider',
            value: apiLink?.provider === 'Starlink' || Number(apiLink?.provider) === 2 
              ? 'Starlink' 
              : apiLink?.provider === 'OneWeb' || Number(apiLink?.provider) === 3
                ? 'OneWeb'
                : getCarrierTypeName(Number(apiLink?.provider)),
          },
          { label: 'Name', value: apiLink?.name || link.name || `eth${link.interfaceName}` || '' },
          { label: 'IP Address', value: apiLink?.ip_address || '' },
          { label: 'MAC Address', value: apiLink?.mac_address || '' },
          {
            label: 'Address Type',
            value: apiLink?.address_type !== undefined ? getAddressTypeName(apiLink.address_type) : '',
          },
          { label: 'Status', value: apiLink?.status !== undefined ? getLinkStatusName(apiLink.status) : '' },
          {
            label: 'Download',
            value: apiLink?.rx_rate !== undefined && apiLink?.rx_rate !== null ? `${apiLink.rx_rate} Mbps` : '',
          },
          {
            label: 'Upload',
            value: apiLink?.tx_rate !== undefined && apiLink?.tx_rate !== null ? `${apiLink.tx_rate} Mbps` : '',
          },
          // Add all metadata fields dynamically
          ...getMetadataFields(apiLink),
        ];

        // Filter out empty values, specifically for IP Address and MAC Address
        const filteredDetailedLinkData = detailedLinkData.filter(item => {
          if (item.label === 'Provider' || item.label === 'Name') {
            return true;
          }
          // For IP Address and MAC Address, only show if they have values
          if ((item.label === 'IP Address' || item.label === 'MAC Address') && (!item.value || item.value === '')) {
            return false;
          }
          return true;
        });

        // Check if satellite controls should be shown - handle both string values and numeric enum values
        const shouldShowSatelliteControls = 
          // Check string values
          apiLink?.provider === 'Starlink' || 
          apiLink?.provider === 'OneWeb' ||
          // Check numeric enum values (CarrierType.Starlink = 2, CarrierType.OneWeb = 3)
          Number(apiLink?.provider) === 2 ||
          Number(apiLink?.provider) === 3;

        return (
          <React.Fragment key={`primary-link-${index}`}>
            <Row className="font-12 mb-3">
              <Col>
                <CustomCard className={`h-100 ${linkClass} ${isStandby ? 'standby-inner-card' : ''}`} isCompact>
                  <CardHeader>Configured Throughput</CardHeader>
                  <CardBody>
                    <div className="w-100">
                      <ArrowCircleDownIcon className="icon-small" /> Download
                      <strong className="ms-3">{link.downloadThroughput} Mbps</strong>
                    </div>
                    <div className="w-100">
                      <ArrowCircleUpIcon className="icon-small" /> Upload
                      <strong className="ms-3">{link.uploadThroughput} Mbps</strong>
                    </div>
                  </CardBody>
                </CustomCard>
              </Col>
              <Col>
                <CustomCard className={`h-100 ${linkClass} ${isStandby ? 'standby-inner-card' : ''}`} isCompact>
                  <CardBody>
                    <InfoSection keyValuePairs={linkData} showHeading={false} />
                  </CardBody>
                </CustomCard>
              </Col>
              <Col>
                <CustomCard className={`h-100 ${linkClass} ${isStandby ? 'standby-inner-card' : ''}`} isCompact>
                  <CardBody>
                    <InfoSection keyValuePairs={filteredDetailedLinkData} showHeading={false} />

                    {shouldShowSatelliteControls && (
                      <div className="d-flex justify-content-center w-100 mt-2">
                        <Button
                          className="ethica-button-green mx-2"
                          style={{ width: '200px' }}
                          onClick={() => openLinkActionConfirm(link.id, LinkCommandType.Reboot, link.name)}
                          isLoading={rebootingLinks[link.id] || false}
                          isDisabled={rebootingLinks[link.id] || false}
                        >
                          Reboot
                        </Button>
                        <Button
                          className="ethica-button-green mx-2"
                          style={{ width: '200px' }}
                          onClick={() =>
                            openLinkActionConfirm(
                              link.id,
                              apiLink?.status === LinkStatus.Up ? LinkCommandType.Unstow : LinkCommandType.Stow,
                              link.name
                            )
                          }
                          isLoading={stowingLinks[link.id] || false}
                          isDisabled={stowingLinks[link.id] || false}
                        >
                          {apiLink?.status === LinkStatus.Up ? 'Unstow' : 'Stow'}
                        </Button>
                      </div>
                    )}
                  </CardBody>
                </CustomCard>
              </Col>
            </Row>
          </React.Fragment>
        );
      });
    },
    [site, expandedLinkId, siteApiData]
  );

  // Handle link reboot
  const handleRebootLink = async (linkId: string) => {
    try {
      setRebootingLinks((prev) => ({ ...prev, [linkId]: true }));

      // Find the link in the API data to get the provider
      const apiLink = siteApiData?.tunnels?.[0]?.links?.find(
        (link) => link.id === linkId || link.interface_name === linkId
      );

      // Determine the dish type based on provider
      const dishType =
        apiLink?.provider === 'starlink' || apiLink?.provider === 'Starlink' || Number(apiLink?.provider) === 2
          ? 'Starlink' 
          : apiLink?.provider === 'oneweb' || apiLink?.provider === 'OneWeb' || Number(apiLink?.provider) === 3
            ? 'OneWeb' 
            : 'Satellite';

      // Call the API service
      const response = await LinkService.sendLinkCommand(
        id!, // site ID from the component with non-null assertion
        linkId,
        LinkCommandType.Reboot
      );

      // Show success message
      dispatch(sessionActionCreators.notificationEnqueue({
        message: 'Reboot command sent successfully.',
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'success',
        },
      }));
    } catch (error: any) {
      // Find the link in the API data to get the provider
      const apiLink = siteApiData?.tunnels?.[0]?.links?.find(
        (link) => link.id === linkId || link.interface_name === linkId
      );

      // Determine the dish type based on provider
      const dishType =
        apiLink?.provider === 'starlink' || apiLink?.provider === 'Starlink' || Number(apiLink?.provider) === 2
          ? 'Starlink' 
          : apiLink?.provider === 'oneweb' || apiLink?.provider === 'OneWeb' || Number(apiLink?.provider) === 3
            ? 'OneWeb' 
            : 'Satellite';

      // Show user-friendly error message
      let errorMessage = '';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Authentication error. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = `You do not have permission to reboot this ${dishType} dish.`;
        } else if (error.response.status === 400) {
          const errorMsg = error.response.data?.error || 'Invalid request format';
          errorMessage = `Bad request: ${errorMsg}`;
        } else if (error.response.status === 404) {
          errorMessage = `Unable to reboot ${dishType} dish. Please refresh the page and try again.`;
        } else {
          errorMessage = `Failed to reboot ${dishType} dish: ${error.response.data?.message || 'Server error'} (Status: ${error.response.status})`;
        }
      } else if (error.message) {
        if (error.message.includes('Network Error')) {
          errorMessage = 'Network connection issue. Please check your internet connection and try again.';
        } else {
          errorMessage = `Failed to reboot ${dishType} dish: ${error.message}`;
        }
      } else {
        errorMessage = `Failed to reboot ${dishType} dish. Please try again.`;
      }

      dispatch(sessionActionCreators.notificationEnqueue({
        message: errorMessage,
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'error',
        },
      }));
    } finally {
      // Reset loading state after a delay to show feedback to user
      setTimeout(() => {
        setRebootingLinks((prev) => ({ ...prev, [linkId]: false }));
      }, 1000);
    }
  };

  // Handle link stow
  const handleStowLink = async (linkId: string) => {
    try {
      setStowingLinks((prev) => ({ ...prev, [linkId]: true }));

      // Find the link in the API data to get the provider
      const apiLink = siteApiData?.tunnels?.[0]?.links?.find(
        (link) => link.id === linkId || link.interface_name === linkId
      );

      // Determine the dish type based on provider
      const dishType =
        apiLink?.provider === 'starlink' || apiLink?.provider === 'Starlink' || Number(apiLink?.provider) === 2
          ? 'Starlink' 
          : apiLink?.provider === 'oneweb' || apiLink?.provider === 'OneWeb' || Number(apiLink?.provider) === 3
            ? 'OneWeb' 
            : 'Satellite';

      // Call the API service
      const response = await LinkService.sendLinkCommand(
        id!, // site ID from the component with non-null assertion
        linkId,
        LinkCommandType.Stow
      );

      // Show success message
      dispatch(sessionActionCreators.notificationEnqueue({
        message: 'Stow command sent successfully.',
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'success',
        },
      }));
    } catch (error: any) {
      // Find the link in the API data to get the provider
      const apiLink = siteApiData?.tunnels?.[0]?.links?.find(
        (link) => link.id === linkId || link.interface_name === linkId
      );

      // Determine the dish type based on provider
      const dishType =
        apiLink?.provider === 'starlink' || apiLink?.provider === 'Starlink' || Number(apiLink?.provider) === 2
          ? 'Starlink' 
          : apiLink?.provider === 'oneweb' || apiLink?.provider === 'OneWeb' || Number(apiLink?.provider) === 3
            ? 'OneWeb' 
            : 'Satellite';

      // Show user-friendly error message
      let errorMessage = '';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Authentication error. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = `You do not have permission to stow this ${dishType} dish.`;
        } else if (error.response.status === 400) {
          const errorMsg = error.response.data?.error || 'Invalid request format';
          errorMessage = `Bad request: ${errorMsg}`;
        } else if (error.response.status === 404) {
          errorMessage = `Unable to stow ${dishType} dish. Please refresh the page and try again.`;
        } else {
          errorMessage = `Failed to stow ${dishType} dish: ${error.response.data?.message || 'Server error'} (Status: ${error.response.status})`;
        }
      } else if (error.message) {
        if (error.message.includes('Network Error')) {
          errorMessage = 'Network connection issue. Please check your internet connection and try again.';
        } else {
          errorMessage = `Failed to stow ${dishType} dish: ${error.message}`;
        }
      } else {
        errorMessage = `Failed to stow ${dishType} dish. Please try again.`;
      }

      dispatch(sessionActionCreators.notificationEnqueue({
        message: errorMessage,
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'error',
        },
      }));
    } finally {
      // Reset loading state after a delay to show feedback to user
      setTimeout(() => {
        setStowingLinks((prev) => ({ ...prev, [linkId]: false }));
        // Refresh the site data to update the button state
        fetchSiteApiData();
      }, 1000);
    }
  };

  // Handle link unstow
  const handleUnstowLink = async (linkId: string) => {
    try {
      setStowingLinks((prev) => ({ ...prev, [linkId]: true }));

      // Find the link in the API data to get the provider
      const apiLink = siteApiData?.tunnels?.[0]?.links?.find(
        (link) => link.id === linkId || link.interface_name === linkId
      );

      // Determine the dish type based on provider
      const dishType =
        apiLink?.provider === 'starlink' || apiLink?.provider === 'Starlink' || Number(apiLink?.provider) === 2
          ? 'Starlink' 
          : apiLink?.provider === 'oneweb' || apiLink?.provider === 'OneWeb' || Number(apiLink?.provider) === 3
            ? 'OneWeb' 
            : 'Satellite';

      // Call the API service
      const response = await LinkService.sendLinkCommand(
        id!, // site ID from the component with non-null assertion
        linkId,
        LinkCommandType.Unstow
      );

      // Show success message
      dispatch(sessionActionCreators.notificationEnqueue({
        message: 'Unstow command sent successfully.',
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'success',
        },
      }));
    } catch (error: any) {
      // Find the link in the API data to get the provider
      const apiLink = siteApiData?.tunnels?.[0]?.links?.find(
        (link) => link.id === linkId || link.interface_name === linkId
      );

      // Determine the dish type based on provider
      const dishType =
        apiLink?.provider === 'starlink' || apiLink?.provider === 'Starlink' || Number(apiLink?.provider) === 2
          ? 'Starlink' 
          : apiLink?.provider === 'oneweb' || apiLink?.provider === 'OneWeb' || Number(apiLink?.provider) === 3
            ? 'OneWeb' 
            : 'Satellite';

      // Show user-friendly error message
      let errorMessage = '';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Authentication error. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = `You do not have permission to unstow this ${dishType} dish.`;
        } else if (error.response.status === 400) {
          const errorMsg = error.response.data?.error || 'Invalid request format';
          errorMessage = `Bad request: ${errorMsg}`;
        } else if (error.response.status === 404) {
          errorMessage = `Unable to unstow ${dishType} dish. Please refresh the page and try again.`;
        } else {
          errorMessage = `Failed to unstow ${dishType} dish: ${error.response.data?.message || 'Server error'} (Status: ${error.response.status})`;
        }
      } else if (error.message) {
        if (error.message.includes('Network Error')) {
          errorMessage = 'Network connection issue. Please check your internet connection and try again.';
        } else {
          errorMessage = `Failed to unstow ${dishType} dish: ${error.message}`;
        }
      } else {
        errorMessage = `Failed to unstow ${dishType} dish. Please try again.`;
      }

      dispatch(sessionActionCreators.notificationEnqueue({
        message: errorMessage,
        options: {
          key: new Date().getTime() + Math.random(),
          variant: 'error',
        },
      }));
    } finally {
      // Reset loading state after a delay to show feedback to user
      setTimeout(() => {
        setStowingLinks((prev) => ({ ...prev, [linkId]: false }));
        // Refresh the site data to update the button state
        fetchSiteApiData();
      }, 1000);
    }
  };

  // Open confirmation dialog
  const openLinkActionConfirm = (linkId: string, action: LinkCommandType, linkName: string) => {
    // Find the link in the API data to get the provider
    const apiLink = siteApiData?.tunnels?.[0]?.links?.find(
      (link) => link.id === linkId || link.interface_name === linkId
    );

    // Convert provider to a user-friendly name
    let providerName = '';
    if (apiLink?.provider === 'Starlink' || apiLink?.provider === 'starlink' || Number(apiLink?.provider) === 2) {
      providerName = 'Starlink';
    } else if (apiLink?.provider === 'OneWeb' || apiLink?.provider === 'oneweb' || Number(apiLink?.provider) === 3) {
      providerName = 'OneWeb';
    } else {
      // Try to convert numeric value if it exists
      if (apiLink?.provider !== undefined) {
        providerName = getCarrierTypeName(Number(apiLink.provider));
      } else {
        providerName = 'Satellite';
      }
    }

    setConfirmLinkAction({
      isOpen: true,
      linkId,
      linkName,
      provider: providerName,
      action,
    });
  };

  // Close confirmation dialog
  const closeLinkActionConfirm = () => {
    setConfirmLinkAction({
      isOpen: false,
      linkId: '',
      linkName: '',
      provider: '',
      action: null,
    });
  };

  // Handle confirmation
  const handleLinkActionConfirm = () => {
    const { linkId, action } = confirmLinkAction;

    if (linkId && action) {
      if (action === LinkCommandType.Reboot) {
        handleRebootLink(linkId);
      } else if (action === LinkCommandType.Stow) {
        handleStowLink(linkId);
      } else if (action === LinkCommandType.Unstow) {
        handleUnstowLink(linkId);
      }
    }

    closeLinkActionConfirm();
  };

  const getStatus = (statusId) => {
    const statuses = [
      { status: 'Offline', color: 'status-text-black' },    // Site has never been power on
      { status: 'Down', color: 'status-text-red' },         // Site is down
      { status: 'Degraded', color: 'status-text-yellow' },   // One or more links are not connected
      { status: 'Online', color: 'status-text-green' },       // Site is online and all links are connected
      { status: 'Offline', color: 'status-text-gray' },     // Site has been offline for more than 2 weeks
    ];
    // Default to status 0 (Offline) if statusId is undefined or invalid
    const validStatusId = (statusId >= 0 && statusId < statuses.length) ? statusId : 0;
    return statuses[validStatusId];
  };

  //Show loading only if no cached data available
  if (contextLoading && !cachedSite) {
    return <Loader />;
  }

  if (isFailed && !cachedSite) {
    return <ErrorView title="Failed to load Site" message="🤖 Failed to load Site" />;
  }

  // Comprehensive site data logging
  if (false && process.env.NODE_ENV === 'development') {
    console.log('🔍 SiteView Data Debug:', {
      // IDs and Loading States
      urlId: id,
      contextId,
      siteId,
      isSuccess,
      contextLoading,
      
      // Data Availability
      reduxSite: !!reduxSite,
      cachedSite: !!cachedSite,
      site: !!site,
      hasFullData,
      hasBasicData,
      
      // Site IDs
      reduxSiteId: reduxSite?.id,
      cachedSiteId: cachedSite?.id,
      
      // Basic Site Properties (available in both cache and Redux)
      siteName: site?.name,
      siteAddress: site?.address1,
      siteStatus: site?.status,
      wanId: site?.wanId,
      clientCompanyId: site?.clientCompanyId,
      
      // Nested Data (only in Redux)
      hasTunnels: !!site?.Tunnels,
      tunnelsCount: site?.Tunnels?.length || 0,
      hasSiteContacts: !!site?.SiteContacts,
      contactsCount: site?.SiteContacts?.length || 0,
      hasSiteLans: !!site?.SiteLans,
      lansCount: site?.SiteLans?.length || 0,
      hasSiteRoutes: !!site?.SiteRoutes,
      routesCount: site?.SiteRoutes?.length || 0,
      hasClientCompany: !!site?.ClientCompany,
      clientCompanyName: site?.ClientCompany?.name,
      
      // Derived Data
      primaryTunnel: !!primaryTunnel,
      primaryTunnelId: primaryTunnel?.id,
      secondaryTunnel: !!secondaryTunnel,
      siteContact: !!siteContact,
      wan: !!wan,
      server: !!server,
      
      // Cache State
      cacheInitialized: mapState.cacheInitialized,
      cachedSitesCount: cachedSites?.length || 0
    });
  }

  // Only show NotFound if we've tried both sources and have no data
  if (!site && !contextLoading) {
    // If we have no site data and not loading, show not found
    return <NotFound />;
  }

  // SAFETY: If we still don't have site data but are not in error state, show loading
  if (!site) {
    return <Loader />;
  }

  const aclProfileInfo = getAclProfileInfo();
  const controllerInfo = getControllerInfo();
  const linkInterfaces = getLinkInterfaceOptions();
  const protocols = getProtocolOptions();
  const directions = getDirectionOptions();

  const durationIsValid = isValidNumber(duration);
  const durationErrorState = durationIsValid
    ? { validated: 'default', errorText: '' }
    : {
        validated: 'error',
        errorText: 'Must be a positive number',
      };
  const occurencesIsValid = isValidNumber(occurences);
  const occurencesErrorState = occurencesIsValid
    ? { validated: 'default', errorText: '' }
    : {
        validated: 'error',
        errorText: 'Must be a positive number',
      };
  // const periodErrorState = period !== '' ? { validated: 'default', errorText: '' } : { validated: 'error', errorText: 'Must be a valid CRON string' };
  const interfaceErrorState =
    speedTestInterface !== ''
      ? {
          validated: 'default',
          errorText: '',
        }
      : { validated: 'error', errorText: '' };
  const protocolErrorState =
    protocol !== ''
      ? { validated: 'default', errorText: '' }
      : {
          validated: 'error',
          errorText: '',
        };
  const directionErrorState =
    direction !== ''
      ? { validated: 'default', errorText: '' }
      : {
          validated: 'error',
          errorText: '',
        };

  const speedTestParamsInvalid =
    durationErrorState.validated === 'error' ||
    occurencesErrorState.validated === 'error' ||
    // periodErrorState.validated === 'error' ||
    interfaceErrorState.validated === 'error' ||
    protocolErrorState.validated === 'error' ||
    directionErrorState.validated === 'error';

  // const ospfConfig = [];
  const bgpConfig = [];
  // const ospf = routingConfig?.ospf_configuration;
  //@ts-ignore
  const bgp = routingConfig?.bgp_configuration;

  // if (ospf) {
  //   for (let i=0; i<ospf.length; i++) {
  //     ospfConfig.push(<div style={{ whiteSpace: 'pre' }} key={i}>{ospf[i]}<br /></div>);
  //   }
  // }
  if (bgp) {
    for (let i = 0; i < bgp.length; i++) {
      bgpConfig.push(
        <div style={{ whiteSpace: 'pre' }} key={i}>
          {bgp[i]}
          <br />
        </div>
      );
    }
  }

  return (
    <React.Fragment>
      {contextLoading && !cachedSite ? ( //Show loading only if no cached data
        <Loader />
      ) : (
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 sites-list ${theme}`}
          title=""
          breadcrumbs={[
            { name: 'Sites', url: '/sites', active: false },
            { name: (site as any)?.name || '', active: true },
          ]}
          actionButton={null}
        >
          <>
            <Row>
              <Col lg={5} md={5} sm={12} xs={12}>
                <div className="p-3 h-100 sites-card-wrapper d-flex flex-column position-relative w-100">
                  <Title headingLevel="h1" size="lg" style={{ paddingRight: '70px', marginBottom: '8px' }}>
                    {(site as any)?.name}
                  </Title>
                  <Row className="w-100">
                    <Col lg={9} md={9} sm={12} xs={12}>
                      <div className="w-100">
                        <strong>Status: </strong>&nbsp;<span className={`${getStatus(siteStatus).color}`}>{getStatus(siteStatus).status}</span>
                        <br />
                        <strong>Site ID: </strong>&nbsp;{siteId}
                        <br />
                        <strong>License ID: </strong>&nbsp;{licenseLoading ? <Spinner size="sm" /> : licenseId}
                      </div>
                      <CustomCard className="mt-3">
                        <CardHeader className="preserve-pwan-text">
                          <IconHeading icon={<NetworkIcon className="icon-medium" />} heading="Private pWAN" />
                        </CardHeader>
                        <CardBody>
                          <InfoSection keyValuePairs={getWanInfo()} showHeading={false} />
                        </CardBody>
                      </CustomCard>
                    </Col>
                    <Col lg={3} md={3} sm={12} xs={12} className="button-column-extend">
                      <div className="d-flex flex-column">
                        <Button 
                          className="text-white w-100 mb-2 ethica-button-green" 
                          onClick={editSite} 
                          style={{ 
                            textAlign: 'center', 
                            padding: '10px'
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          className="text-white w-100 mb-2 ethica-button-green"
                          onClick={(e) => setConfirmOpen({ rebootType: 'Site', isOpen: true })}
                          style={{ 
                            textAlign: 'center', 
                            padding: '10px'
                          }}
                        >
                          Reboot Site
                        </Button>
                        {(site?.isServerless || wan) && (
                          <Button
                            className="text-white w-100 mb-2 ethica-button-green"
                            onClick={(e) => setConfirmOpen({ rebootType: 'Tunnel Gateway', isOpen: true })}
                            style={{ 
                              textAlign: 'center', 
                              padding: '10px'
                            }}
                          >
                            Reboot Gateway
                          </Button>
                        )}
                        <Button 
                          className="text-white w-100 mb-2 ethica-button-green" 
                          onClick={launchGrafanaDashboard} 
                          style={{ 
                            textAlign: 'center', 
                            padding: '10px'
                          }}
                        >
                          Performance
                        </Button>
                        <Button 
                          className="text-white w-100 mb-2 ethica-button-green" 
                          onClick={scrollToSpeedTest} 
                          style={{ 
                            textAlign: 'center', 
                            padding: '10px'
                          }}
                        >
                          Speed Test
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col lg={7} md={7} sm={12} xs={12}>
                <div className="p-3 h-100 sites-card-wrapper w-100">
                  <Tabs activeKey={infoTabIndex} onSelect={(_e, idx) => setInfoTabIndex(idx as number)} className="font-12" isBox>
                    <Tab eventKey={0} title={<TabTitleText>Info</TabTitleText>}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', paddingTop: '15px', minHeight: '320px' }} className="w-100 font-12">
                        <div>
                          <CustomCard isCompact style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardHeader className="p-1 fw-bold d-flex align-items-center" style={{ minHeight: '40px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                              <div className="w-100">
                                <IconHeading icon={<EnterpriseIcon className="icon-medium" />} heading="Company Profile" />
                              </div>
                            </CardHeader>
                            <CardBody className="p-2" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                              <InfoSection keyValuePairs={getCompanyInfo()} showHeading={false} />
                              <CustomCard isCompact className="mt-2" style={{ flex: 1 }}>
                                <CardHeader className="p-1 fw-bold d-flex align-items-center" style={{ minHeight: '32px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                                  <div className="w-100">
                                    <IconHeading icon={<CatalogIcon className="icon-medium" />} heading="Company Notes" />
                                  </div>
                                </CardHeader>
                                <CardBody className="p-2">
                                  {getQuickLinks()}
                                  {site?.ClientCompany?.notes && site?.ClientCompany.notes.length > 0 ? (
                                    <div className="textarea-input">
                                      <strong>Notes:</strong> <br />
                                      {site?.ClientCompany.notes}
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </CardBody>
                              </CustomCard>
                            </CardBody>
                          </CustomCard>
                        </div>
                        <div>
                          <CustomCard isCompact style={{ height: '100%' }}>
                            <CardHeader className="p-1 fw-bold d-flex align-items-center" style={{ minHeight: '40px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                              <div className="w-100">
                                <IconHeading icon={<MapMarkerIcon className="icon-medium" />} heading="Information" />
                              </div>
                            </CardHeader>
                            <CardBody className="p-2">
                              <InfoSection keyValuePairs={getLocationInfo()} showHeading={false} />
                            </CardBody>
                          </CustomCard>
                        </div>
                        <div>
                          <CustomCard isCompact style={{ height: '100%' }}>
                            <CardHeader className="p-1 fw-bold d-flex align-items-center" style={{ minHeight: '40px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                              <div className="w-100">
                                <IconHeading icon={<UserIcon className="icon-medium" />} heading="Contact Information" />
                              </div>
                            </CardHeader>
                            <CardBody className="p-2">
                              <InfoSection keyValuePairs={getContactInfo()} showHeading={false} />
                            </CardBody>
                          </CustomCard>
                        </div>
                      </div>
                    </Tab>
                    <Tab eventKey={1} title={<TabTitleText>Downloads</TabTitleText>}>
                      <div className="w-100 font-12" style={{ paddingTop: '15px', minHeight: '320px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          {downloadItems.map((item, index) => {
                            const downloadsLocked = !!activeDownloadTitle;
                            return (
                            <div
                              key={item.title}
                              className="py-2"
                              style={{ borderBottom: index < downloadItems.length - 1 ? '1px solid #dee2e6' : 'none' }}
                            >
                              <div className="d-flex align-items-center mb-2">
                                <ArrowCircleDownIcon className="icon-medium" style={{ marginRight: '8px', color: downloadsLocked ? '#8a8d90' : '#6a9b1b' }} />
                                <span style={{ fontWeight: 600, color: downloadsLocked ? '#8a8d90' : '#2f3133' }}>{item.title}</span>
                              </div>
                              <div className="d-flex align-items-center" style={{ gap: '16px', paddingLeft: '28px' }}>
                                {item.links.map((link) => (
                                  <DownloadImage
                                    key={link.type}
                                    imageType="site"
                                    imageId={siteId}
                                    downloadTypeOverride={link.type}
                                    renderAsLink
                                    linkLabel={link.label}
                                    hideStatus
                                    disabled={downloadsLocked}
                                    isActiveDownload={activeDownloadTitle === link.title}
                                    cancelRequestId={cancelDownloadRequestId}
                                    onDownloadStart={() => handleDownloadStart(link.title)}
                                    onCancelComplete={handleCancelComplete}
                                    onStatusChange={(update) => handleDownloadStatusChange(link.title, update)}
                                  />
                                ))}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                        <div className="d-flex align-items-center justify-content-between" style={{ minHeight: '24px', paddingTop: '12px', borderTop: '1px solid #dee2e6', color: '#4f5255', gap: '12px' }}>
                          <div>
                            {downloadFooterStatus.status ? `${downloadFooterStatus.title}: ${downloadFooterStatus.status}` : '\u00A0'}
                          </div>
                          {activeDownloadTitle ? (
                            <Button variant="primary" size="sm" className="ethica-button-cancel" onClick={handleCancelDownload}>
                              Cancel
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </Tab>
                  </Tabs>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12} md={12} sm={12} xs={12}>
                <div className="p-3 w-100 h-100 sites-card-wrapper d-flex justify-content-between">
                  <CustomCard isCompact className={`mb-3 w-100`}>
                    <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                      <div className="w-100 d-flex justify-content-between">
                        <IconHeading icon={<MdAccountTree className="icon-medium" />} heading="LAN Interfaces" />
                      </div>
                    </CardHeader>
                    <CardBody>
                      {siteLansLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
                          <Spinner size="lg" />
                          <span className="ms-2">Loading LAN interfaces...</span>
                        </div>
                      ) : (
                        <React.Fragment>
                          {(getLans()?.length ?? 0) > 0 ? getLans() : <div>There are no LANs configured</div>}
                        </React.Fragment>
                      )}
                    </CardBody>
                  </CustomCard>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12} md={12} sm={12} xs={12}>
                <div className="p-3 w-100 h-100 sites-card-wrapper d-flex justify-content-between font-12">
                  <CustomCard isCompact className={`mb-3 w-100`}>
                    <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                      <div className="w-100 d-flex justify-content-between">
                        <IconHeading icon={<MdAltRoute className="icon-medium" />} heading="Routing" />
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col>
                          <CustomCard isCompact className={`mb-3 w-100 h-100`}>
                            <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                              <div className="w-100 d-flex justify-content-between">
                                <div className="w-100">
                                  <IconHeading
                                    icon={<MdAppBlocking className="icon-medium" />}
                                    heading="Routed IP Subnets"
                                    subHeading={routedIpState}
                                  />
                                </div>
                              </div>
                            </CardHeader>
                            <CardBody>
                              {contextLoading && !cachedSite ? ( // Show loading only if no cached data
                                <Spinner size="lg" />
                              ) : (
                                <>
                                  <CustomCard>
                                    <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                                      <div className="w-100 d-flex justify-content-between">
                                        <IconHeading
                                          icon={<MdNewLabel className="icon-medium" />}
                                          heading="Assign Routed IP Addresses"
                                        />
                                      </div>
                                    </CardHeader>
                                    <CardBody>
                                      {(getRoutedIpInfo()?.length ?? 0) > 0 ? (
                                        <div>
                                          <Spacer />
                                          <div className="right">
                                            <strong>Router IP</strong>
                                          </div>
                                          <div>
                                            <strong>Public Subnet</strong>
                                          </div>
                                          <Spacer />
                                          <InfoSection keyValuePairs={getRoutedIpInfo()} showHeading={false} />
                                        </div>
                                      ) : (
                                        <div>No routes defined</div>
                                      )}
                                    </CardBody>
                                  </CustomCard>
                                </>
                              )}
                            </CardBody>
                          </CustomCard>
                        </Col>

                        <Col>
                          <CustomCard isCompact className="h-100">
                            <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                              <div className="w-100 d-flex justify-content-between">
                                <div className="w-100">
                                  <IconHeading
                                    icon={<TopologyIcon className="icon-medium" />}
                                    heading="Advanced"
                                    subHeading={
                                      //@ts-ignore
                                      !routingConfig.bgp_configuration ? 'disabled' : ''
                                    }
                                  />
                                </div>
                              </div>
                            </CardHeader>
                            <CardBody>
                              <CustomCard>
                                <CardHeader>
                                  <div className="w-100 d-flex justify-content-between">
                                    <IconHeading
                                      icon={<PficonNetworkRangeIcon className="icon-medium" />}
                                      heading="BGP"
                                    />
                                  </div>
                                </CardHeader>
                                <CardBody>{bgpConfig}</CardBody>
                              </CustomCard>
                            </CardBody>
                          </CustomCard>
                        </Col>
                      </Row>
                    </CardBody>
                  </CustomCard>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12} md={12} sm={12} xs={12}>
                <div className="p-3 w-100 h-100 sites-card-wrapper d-flex justify-content-between font-12">
                  <CustomCard isCompact className={`mb-3 w-100`}>
                    <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                      <div className="w-100 d-flex justify-content-between">
                        <IconHeading icon={<MdForkLeft className="icon-medium" />} heading="Tunnel Settings" />
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col>
                          <CustomCard isCompact className="tunnel-settings-row">
                            <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                              <div className="w-100 d-flex justify-content-between">
                                <IconHeading
                                  icon={<InfrastructureIcon className="icon-medium" />}
                                  heading="Controllers"
                                />
                              </div>
                            </CardHeader>
                            <CardBody>
                              {contextLoading && !cachedSite ? ( // Show loading only if no cached data
                                <Spinner size="lg" />
                              ) : (
                                <div>
                                  <InfoSection keyValuePairs={controllerInfo} showHeading={false} />
                                </div>
                              )}
                            </CardBody>
                          </CustomCard>
                        </Col>

                        <Col>
                          <CustomCard isCompact className="tunnel-settings-row">
                            <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                              <div className="w-100 d-flex justify-content-between">
                                <IconHeading icon={<DomainIcon className="icon-medium" />} heading="Tunnel Settings" />
                              </div>
                            </CardHeader>
                            <CardBody>
                              {contextLoading && !cachedSite ? ( // Show loading only if no cached data
                                <Spinner size="lg" />
                              ) : (
                                <div>
                                  <InfoSection keyValuePairs={getTunnelInfo()} showHeading={false} />
                                </div>
                              )}
                            </CardBody>
                          </CustomCard>
                        </Col>

                        <Col>
                          <CustomCard isCompact className="tunnel-settings-row">
                            <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                              <div className="w-100 d-flex justify-content-between">
                                <IconHeading
                                  icon={<WrenchIcon className="icon-medium" />}
                                  heading="Expected Throughput"
                                />
                              </div>
                            </CardHeader>
                            <CardBody>
                              {contextLoading && !cachedSite ? ( // Show loading only if no cached data
                                <Spinner size="lg" />
                              ) : (
                                <div>
                                  {(getLinks(false)?.length ?? 0) > 0 || (getLinks(true)?.length ?? 0) > 0 ? (
                                    throughputFromLinks(site?.Tunnels[0].Links)
                                  ) : (
                                    <div>There are no active links configured!</div>
                                  )}
                                </div>
                              )}
                            </CardBody>
                          </CustomCard>
                        </Col>
                      </Row>
                    </CardBody>
                  </CustomCard>
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="p-3 w-100 h-100 sites-card-wrapper d-flex justify-content-between font-12">
                  <CustomCard isCompact className={`mb-3 w-100`}>
                    <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                      <div className="w-100 d-flex justify-content-between">
                        <IconHeading icon={<MdOutlineSecurity className="icon-medium" />} heading="Security Settings" />
                      </div>
                    </CardHeader>
                    <CardBody>
                      <CustomCard>
                        <CardHeader>
                          <div className="w-100 d-flex justify-content-between">
                            <div className="w-100">
                              <IconHeading
                                icon={<ServiceCatalogIcon className="icon-medium" />}
                                heading="ACL Profiles"
                                subHeading={aclProfileInfo?.length > 0 ? 'Enabled' : 'Disabled'}
                              />
                            </div>
                          </div>
                        </CardHeader>
                        <CardBody>
                          {aclProfileInfo?.length > 0 ? (
                            <div>
                              <InfoSection keyValuePairs={aclProfileInfo} showHeading={false} />
                            </div>
                          ) : (
                            <div>No ACL Profiles assigned!</div>
                          )}
                        </CardBody>
                      </CustomCard>
                    </CardBody>
                  </CustomCard>
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="p-3 w-100 h-100 sites-card-wrapper d-flex justify-content-between font-12">
                  {/* Primary Interfaces Card */}
                  {(getLinks(false)?.length ?? 0) > 0 && (
                    <CustomCard isCompact className={`mb-3 w-100`}>
                      <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                        <div className="w-100 d-flex justify-content-between">
                          <IconHeading icon={<MdInsertLink className="icon-medium" />} heading="Primary Interfaces" />
                        </div>
                      </CardHeader>
                      <CardBody>
                        {getLinks(false)}
                      </CardBody>
                    </CustomCard>
                  )}
                  {/* Standby Interfaces Card */}
                  {(getLinks(true)?.length ?? 0) > 0 && (
                    <CustomCard isCompact className={`mb-3 w-100 standby-card`}>
                      <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                        <div className="w-100 d-flex justify-content-between">
                          <IconHeading icon={<MdInsertLink className="icon-medium" />} heading="Standby Interfaces" />
                        </div>
                      </CardHeader>
                      <CardBody>
                        {getLinks(true)}
                      </CardBody>
                    </CustomCard>
                  )}
                  {/* If no links at all, show message */}
                  {(getLinks(false)?.length ?? 0) === 0 && (getLinks(true)?.length ?? 0) === 0 && (
                    <CustomCard isCompact className={`mb-3 w-100`}>
                      <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                        <div className="w-100 d-flex justify-content-between">
                          <IconHeading icon={<MdInsertLink className="icon-medium" />} heading="Internet Interfaces" />
                        </div>
                      </CardHeader>
                      <CardBody>
                        <div>
                          There are no Primary or Standby links configured
                          <br />
                          <br />
                        </div>
                      </CardBody>
                    </CustomCard>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="p-3 w-100 h-100 sites-card-wrapper d-flex justify-content-between font-12">
                  <CustomCard isCompact className={`mb-3 w-100`}>
                    <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                      <div className="w-100 d-flex justify-content-between">
                        <IconHeading icon={<AutomationIcon className="icon-medium" />} heading="Business Continuity" />
                      </div>
                    </CardHeader>
                    <CardBody>
                      <CustomCard>
                        <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                          <div className="icon-heading-container">
                            <div className="icon-heading">A last-resort pass-through interface if tunnel fails</div>
                          </div>
                        </CardHeader>
                        <CardBody>
                          <InfoSection keyValuePairs={getBusinessContinuityInfo()} showHeading={false} />
                        </CardBody>
                      </CustomCard>
                    </CardBody>
                  </CustomCard>
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="p-3 w-100 h-100 sites-card-wrapper d-flex justify-content-between">
                  <CustomCard isCompact className={`mb-3 w-100`}>
                    <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                      <div className="w-100 d-flex justify-content-between" ref={speedTestRef}>
                        <IconHeading icon={<OutlinedClockIcon className="icon-medium" />} heading="Speed Tests" />
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col>
                          <CustomCard className="h-100">
                            <CardHeader className="font-12">
                              Start a speed test with the default settings, or expand below for advanced options
                            </CardHeader>
                            <CardBody>
                              <Accordion asDefinitionList>
                                <AccordionItem>
                                  <AccordionToggle
                                    onClick={() => {
                                      onAccordionToggle('advanced-options');
                                    }}
                                    isExpanded={advancedExpanded === 'advanced-options'}
                                    id="advanced-options"
                                  >
                                    Advanced options
                                  </AccordionToggle>
                                  <AccordionContent
                                    id="advanced-expand"
                                    isHidden={advancedExpanded !== 'advanced-options'}
                                  >
                                    <table>
                                      <tbody>
                                        <tr>
                                          <td>
                                            <FormGroup
                                              label="Number of Runs"
                                              isRequired
                                              helperTextInvalid=" Required Field"
                                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                                              //@ts-ignore
                                              validated={occurencesErrorState.validated}
                                              className="form-item me-3"
                                            >
                                              <TextInput
                                                type="text"
                                                id="occurences"
                                                name="occurences"
                                                //@ts-ignore
                                                validated={occurencesErrorState.validated}
                                                value={occurences}
                                                onChange={handleOccurencesChange}
                                                className="form-control"
                                              />
                                            </FormGroup>
                                          </td>
                                          <td>
                                            <FormGroup
                                              label="Duration(sec)"
                                              isRequired
                                              helperTextInvalid=" Required Field"
                                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                                              //@ts-ignore
                                              validated={durationErrorState.validated}
                                              className="form-item me-3"
                                            >
                                              <TextInput
                                                type="text"
                                                id="duration"
                                                name="duration"
                                                //@ts-ignore
                                                validated={durationErrorState.validated}
                                                value={duration}
                                                onChange={handleDurationChange}
                                                className="form-control"
                                              />
                                            </FormGroup>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>
                                            <FormGroup
                                              label="Interface"
                                              isRequired
                                              helperTextInvalid=""
                                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                                              validated={interfaceErrorState.validated as any}
                                            >
                                              <FormSelect
                                                id="interface-select"
                                                value={speedTestInterface}
                                                onChange={onInterfaceSelect}
                                                isDisabled={speedTestInputsDisabled}
                                              >
                                                <FormSelectOption key="default" value="" label="Select Interface..." isDisabled />
                                                {getLinkInterfaceOptions()}
                                              </FormSelect>
                                            </FormGroup>
                                          </td>
                                          <td>
                                            <FormGroup
                                              label="Protocol"
                                              isRequired
                                              helperTextInvalid=""
                                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                                              validated={protocolErrorState.validated as any}
                                            >
                                              <FormSelect
                                                id="protocol-select"
                                                value={protocol}
                                                onChange={onProtocolSelect}
                                              >
                                                <FormSelectOption key="default" value="" label="Select Protocol..." isDisabled />
                                                {getProtocolOptions()}
                                              </FormSelect>
                                            </FormGroup>
                                          </td>
                                          <td>
                                            <FormGroup
                                              label="Direction"
                                              isRequired
                                              helperTextInvalid=""
                                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                                              validated={directionErrorState.validated as any}
                                            >
                                              <FormSelect
                                                id="direction-select"
                                                value={direction}
                                                onChange={onDirectionSelect}
                                              >
                                                <FormSelectOption key="default" value="" label="Select Direction..." isDisabled />
                                                {getDirectionOptions()}
                                              </FormSelect>
                                            </FormGroup>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    <br />
                                  </AccordionContent>
                                </AccordionItem>
                              </Accordion>

                              {speedTestIsRunning ? (
                                <div>
                                  <br />
                                  <Spinner size="md" /> &nbsp;Speed Test is running. Please wait ...
                                  <br />
                                </div>
                              ) : (
                                <></>
                              )}

                              {speedTestResultStatus === 'Completed' ? (
                                <div>
                                  <br />
                                  Speed Test on <strong>{completedSpeedTestInterface}</strong> completed successfully!
                                  <br />
                                  <br />
                                </div>
                              ) : (
                                <></>
                              )}

                              {speedTestResultStatus === 'Cancelled' ? (
                                <div>
                                  <br />
                                  Speed Test cancelled successfully
                                </div>
                              ) : (
                                <></>
                              )}

                              {speedTestResultStatus === 'Failed' ? (
                                <div>
                                  <br />
                                  Speed Test failed to complete
                                </div>
                              ) : (
                                <></>
                              )}

                              {speedTestResultStatus === 'StartFailed' ? (
                                <div>
                                  <br />
                                  The Speed Test failed to start due to an error, or there may already be one scheduled
                                  or running. Please try again later
                                </div>
                              ) : (
                                <></>
                              )}

                              {speedTestResults?.download > 0 || speedTestResults?.upload > 0 ? (
                                <div>
                                  <br />
                                  <table>
                                    <tbody>
                                      <tr>
                                        <td>
                                          <div>
                                            <strong>Download</strong>
                                            <br />
                                            <span className="peak">{getMbps(speedTestResults.download)} Mbps</span>
                                          </div>
                                        </td>
                                        <td width="50"></td>
                                        <td>
                                          <div>
                                            <strong>Upload</strong>
                                            <br />
                                            <span className="peak">{getMbps(speedTestResults.upload)} Mbps</span>
                                          </div>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>

                                  <Spacer />
                                  <hr className="hr-results" />
                                  <Spacer />

                                  <table>
                                    <tbody>
                                      <tr>
                                        <td width="175">
                                          <strong>Latency:</strong> {speedTestResults.latency} ms
                                        </td>
                                        <td width="175">
                                          <strong>Jitter:</strong> {speedTestResults.jitter} ms
                                        </td>
                                        <td width="175">
                                          <strong>Loss:</strong> {speedTestResults.loss}%
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <></>
                              )}

                              <Button
                                variant="primary"
                                className="me-2 ethica-button-green"
                                isDisabled={speedTestParamsInvalid || speedTestIsRunning}
                                onClick={runSpeedTest}
                              >
                                Start
                              </Button>
                              <Button variant="link" className="ethica-button-black" onClick={cancelSpeedTestConfirm}>
                                {cancelButtonText}
                              </Button>
                            </CardBody>
                          </CustomCard>
                        </Col>
                        <Col>
                          <CustomCard className="h-100">
                            <CardBody>
                              {isSpeedTestsLoading ? (
                                <Spinner size="md" />
                              ) : speedTests.length === 0 ? (
                                <div>No Speed Tests found!</div>
                              ) : (
                                <div>
                                  <PatternflyTable
                                    columns={getTestColumns}
                                    perPage={6}
                                    data={speedTests.map(speedTestFromItem)}
                                    page={1}
                                  />
                                </div>
                              )}
                            </CardBody>
                          </CustomCard>
                        </Col>
                      </Row>
                    </CardBody>
                  </CustomCard>
                </div>
              </Col>
            </Row>

            <Modal
              title="Confirm Reboot"
              titleIconVariant={BullhornIcon}
              variant={ModalVariant.small}
              isOpen={confirmOpen.isOpen}
              onClose={() => setConfirmOpen({ isOpen: false, rebootType: '' })}
              actions={[
                <Button
                  key="confirm"
                  variant="primary"
                  onClick={confirmOpen.rebootType === 'Site' ? rebootSite : rebootGateway}
                >
                  Yes
                </Button>,
                <Button key="cancel" variant="link" onClick={() => setConfirmOpen({ isOpen: false, rebootType: '' })}>
                  Cancel
                </Button>,
              ]}
            >
              Are you sure you want to reboot this {confirmOpen.rebootType}?
            </Modal>

            <Modal
              title="Run Speed Test"
              titleIconVariant={OutlinedClockIcon}
              variant={ModalVariant.medium}
              isOpen={isSpeedTestModalOpen}
              //@ts-ignore
              onClose={cancelSpeedTestConfirm}
              actions={[
                <Button
                  key="confirm"
                  variant="primary"
                  isDisabled={speedTestParamsInvalid || speedTestIsRunning}
                  onClick={runSpeedTest}
                >
                  Start
                </Button>,
                <Button key="cancel" variant="link" onClick={cancelSpeedTestConfirm}>
                  {cancelButtonText}
                </Button>,
              ]}
            >
              <p>Start a speed test with the default settings, or expand below for advanced options</p>
              <br />

              <Accordion asDefinitionList>
                <AccordionItem>
                  <AccordionToggle
                    onClick={() => {
                      onAccordionToggle('advanced-options');
                    }}
                    isExpanded={advancedExpanded === 'advanced-options'}
                    id="advanced-options"
                  >
                    Advanced options
                  </AccordionToggle>
                  <AccordionContent id="advanced-expand" isHidden={advancedExpanded !== 'advanced-options'}>
                    <table>
                      <tbody>
                        <tr>
                          <td>
                            <FormGroup
                              label="Number of Runs"
                              isRequired
                              helperTextInvalid=" Required Field"
                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                              //@ts-ignore
                              validated={occurencesErrorState.validated}
                              className="form-item"
                            >
                              <TextInput
                                type="text"
                                id="occurences"
                                name="occurences"
                                //@ts-ignore
                                validated={occurencesErrorState.validated}
                                value={occurences}
                                onChange={handleOccurencesChange}
                                className="form-control"
                              />
                            </FormGroup>
                          </td>
                          <td>
                            <FormGroup
                              label="Duration(sec)"
                              isRequired
                              helperTextInvalid=" Required Field"
                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                              //@ts-ignore
                              validated={durationErrorState.validated}
                              className="form-item"
                            >
                              <TextInput
                                type="text"
                                id="duration"
                                name="duration"
                                //@ts-ignore
                                validated={durationErrorState.validated}
                                value={duration}
                                onChange={handleDurationChange}
                                className="form-control"
                              />
                            </FormGroup>
                          </td>
                          <td>
                            {/* <FormGroup
                                  label="Period(CRON)" isRequired
                                  helperTextInvalid=" Required Field"
                                  helperTextInvalidIcon={<ExclamationCircleIcon />}
                                  // validated={periodErrorState.validated}
                                >
                                  <TextInput
                                    type="text" id="period" name="period"
                                    // validated={periodErrorState.validated}
                                    placeholder="* * * * *"
                                    value={period}
                                    onChange={handlePeriodChange}
                                  />
                                </FormGroup> */}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <FormGroup
                              label="Interface"
                              isRequired
                              helperTextInvalid=""
                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                              //@ts-ignore
                              validated={interfaceErrorState.validated}
                            >
                              <FormSelect
                                id="interface-select"
                                value={speedTestInterface}
                                onChange={onInterfaceSelect}
                                isDisabled={speedTestInputsDisabled}
                              >
                                <FormSelectOption key="default" value="" label="Select Interface..." isDisabled />
                                {getLinkInterfaceOptions()}
                              </FormSelect>
                            </FormGroup>
                          </td>
                          <td>
                            <FormGroup
                              label="Protocol"
                              isRequired
                              helperTextInvalid=""
                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                              //@ts-ignore
                              validated={protocolErrorState.validated}
                            >
                              <FormSelect
                                id="protocol-select"
                                value={protocol}
                                onChange={onProtocolSelect}
                              >
                                <FormSelectOption key="default" value="" label="Select Protocol..." isDisabled />
                                <FormSelectOption key="tcp" value="TCP" label="TCP" />
                                <FormSelectOption key="udp" value="UDP" label="UDP" />
                              </FormSelect>
                            </FormGroup>
                          </td>
                          <td>
                            <FormGroup
                              label="Direction"
                              isRequired
                              helperTextInvalid=""
                              helperTextInvalidIcon={<ExclamationCircleIcon />}
                              //@ts-ignore
                              validated={directionErrorState.validated}
                            >
                              <FormSelect
                                id="direction-select"
                                value={direction}
                                onChange={onDirectionSelect}
                              >
                                <FormSelectOption key="default" value="" label="Select Direction..." isDisabled />
                                <FormSelectOption key="upload" value="Upload" label="Upload" />
                                <FormSelectOption key="download" value="Download" label="Download" />
                                <FormSelectOption key="bidirectional" value="Bidirectional" label="Bidirectional" />
                              </FormSelect>
                            </FormGroup>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <br />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {speedTestIsRunning ? (
                <div>
                  <br />
                  <Spinner size="md" /> &nbsp;Speed Test is running. Please wait ...
                  <br />
                </div>
              ) : (
                <></>
              )}

              {speedTestResultStatus === 'Completed' ? (
                <div>
                  <br />
                  Speed Test on <strong>{completedSpeedTestInterface}</strong> completed successfully!
                  <br />
                  <br />
                </div>
              ) : (
                <></>
              )}

              {speedTestResultStatus === 'Cancelled' ? (
                <div>
                  <br />
                  Speed Test cancelled successfully
                </div>
              ) : (
                <></>
              )}

              {speedTestResultStatus === 'Failed' ? (
                <div>
                  <br />
                  Speed Test failed to complete
                </div>
              ) : (
                <></>
              )}

              {speedTestResultStatus === 'StartFailed' ? (
                <div>
                  <br />
                  The Speed Test failed to start due to an error, or there may already be one scheduled or running.
                  Please try again later
                </div>
              ) : (
                <></>
              )}

              {speedTestResults?.download > 0 || speedTestResults?.upload > 0 ? (
                <div>
                  <br />
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <div>
                            <strong>Download</strong>
                            <br />
                            <span className="peak">{getMbps(speedTestResults.download)} Mbps</span>
                          </div>
                        </td>
                        <td width="50"></td>
                        <td>
                          <div>
                            <strong>Upload</strong>
                            <br />
                            <span className="peak">{getMbps(speedTestResults.upload)} Mbps</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <Spacer />
                  <hr className="hr-results" />
                  <Spacer />

                  <table>
                    <tbody>
                      <tr>
                        <td width="175">
                          <strong>Latency:</strong> {speedTestResults.latency} ms
                        </td>
                        <td width="175">
                          <strong>Jitter:</strong> {speedTestResults.jitter} ms
                        </td>
                        <td width="175">
                          <strong>Loss:</strong> {speedTestResults.loss}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <></>
              )}
            </Modal>

            <Modal
              title="Speed Test Results"
              titleIconVariant={OutlinedClockIcon}
              variant={ModalVariant.large}
              isOpen={isSpeedTestResultsModalOpen}
              onClose={handleSpeedTestResultsToggle}
              actions={[
                <Button
                  key="cancel"
                  className="ethica-button-black"
                  variant="link"
                  onClick={handleSpeedTestResultsToggle}
                >
                  Close
                </Button>,
              ]}
            >
              <table>
                <tbody>
                  <tr>
                    <td width="150">
                      <strong>Peak Download:</strong>
                    </td>
                    <td width="110">{getMbps(download)} Mbps</td>
                    <td width="90">
                      <strong>Device:</strong>
                    </td>
                    <td width="100">{speedTestInterface}</td>
                    <td width="100">
                      <strong>Duration:</strong>
                    </td>
                    <td width="80">{duration}</td>
                  </tr>
                  <tr>
                    <td width="120">
                      <strong>Peak Upload:</strong>
                    </td>
                    <td width="110">{getMbps(upload)} Mbps</td>
                    <td width="90">
                      <strong>Protocol:</strong>
                    </td>
                    <td width="100">{protocol}</td>
                    <td width="90">
                      <strong>Direction:</strong>
                    </td>
                    <td width="145">{direction}</td>
                  </tr>
                  <tr>
                    <td width="145">
                      <strong>Number of Runs:</strong>
                    </td>
                    <td width="80">{occurences}</td>
                  </tr>
                </tbody>
              </table>
              <br />

              {samples?.length ? (
                <div>
                  <strong>Samples:</strong>

                  <PatternflyTable
                    columns={() => getSampleColumns(true)}
                    data={samples.map((sample) => sampleFromItem(sample, true))}
                    page={1}
                  />
                </div>
              ) : (
                <div>No samples to display!</div>
              )}
            </Modal>

            <Modal
              title="Confirm Action"
              titleIconVariant={BullhornIcon}
              variant={ModalVariant.small}
              isOpen={isCancelSpeedTestConfirmOpen}
              onClose={handleCancelSpeedTestConfirmToggle}
              actions={[
                <Button key="confirm" variant="primary" onClick={cancelSpeedTest} className="ethica-button-green">
                  Yes
                </Button>,
                <Button key="cancel" variant="primary" onClick={handleCancelSpeedTestConfirmToggle} className="ethica-button-cancel">
                  Cancel
                </Button>,
              ]}
            >
              <p>Are you sure you want to cancel this speed test?</p>
            </Modal>
          </>
        </Page>
      )}

      {/* Link Action Confirmation Modal */}
      <Modal
        variant={ModalVariant.small}
        title={
          confirmLinkAction.action === LinkCommandType.Reboot
            ? 'Confirm Reboot'
            : confirmLinkAction.action === LinkCommandType.Stow
            ? 'Confirm Stow'
            : 'Confirm Unstow'
        }
        isOpen={confirmLinkAction.isOpen}
        onClose={closeLinkActionConfirm}
        className="modal-fade-in"
        actions={[
          <Button key="confirm" variant="primary" onClick={handleLinkActionConfirm} className="ethica-button-green">
            Confirm
          </Button>,
          <Button key="cancel" variant="primary" onClick={closeLinkActionConfirm} className="ethica-button-cancel">
            Cancel
          </Button>,
        ]}
      >
        Are you sure you want to{' '}
        {confirmLinkAction.action === LinkCommandType.Reboot
          ? 'reboot'
          : confirmLinkAction.action === LinkCommandType.Stow
          ? 'stow'
          : 'unstow'}{' '}
        the {confirmLinkAction.provider} dish connected to {confirmLinkAction.linkName || 'this link'}? This action
        cannot be undone.
      </Modal>
    </React.Fragment>
  );
};

export default SiteView;
